export const PYTHON_ADVANCED_PROBLEMS = [
  {
    id: 1,
    title: "Custom Retry Decorator with Arguments",
    slug: "python-custom-retry-decorator-arguments",
    seoTitle: "Python Custom Retry Decorator | Decorators | Repractiq",
    metaDescription: "Learn how to build advanced decorators that accept configuration arguments to retry failing functions.",
    tags: ["decorators"],
    difficulty: "Hard",
    type: "function",
    description: "Write a decorator factory function called `retry(max_attempts, exceptions)` that wraps a function. It should attempt to execute the function up to `max_attempts` times if any exception specified in the `exceptions` tuple occurs. If it fails on the final attempt, it should re-raise the caught exception.",
    explanation: "Advanced decorators require three layers of function nesting: the outer factory function to accept configuration arguments, a middle wrapper to accept the target function, and an inner function to handle runtime execution logic and loops.",
    hint: "Use `functools.wraps` to preserve function metadata, and loop up to `max_attempts` inside a try-except block.",
    starterCode: `import functools\n\ndef retry(max_attempts, exceptions):\n    def decorator(func):\n        # your code here\n        pass\n    return decorator\n`,
    expectedOutput: "",
    testCases: [
      { input: "3, (ValueError,)", call: "@retry(3, (ValueError,))\ndef test(c=[]):\n c.append(1)\n if len(c) < 3: raise ValueError('err')\n return 'ok'\ntest()", expected: "ok" },
    ],
  },
  {
    id: 2,
    title: "Stateful Data Pipeline Generator",
    slug: "python-stateful-data-pipeline-generator",
    seoTitle: "Python Generator Pipeline Streams | Generators | Repractiq",
    metaDescription: "Build memory-efficient streaming pipelines using yield statements and generator chaining expressions.",
    tags: ["generators"],
    difficulty: "Hard",
    type: "function",
    description: "Write a generator function called `windowed_averages` that takes an iterable stream of numbers and a window size `k`. The generator should maintain a sliding window of the last `k` items and yield the calculated floating-point average for every full window processed.",
    explanation: "Generators preserve execution state between yields. Chaining streaming generators allows you to process infinitely long datasets without accumulating excessive items in system memory.",
    hint: "Use a collections.deque object with maxlen=k to efficiently keep track of the sliding numbers.",
    starterCode: `from collections import deque\n\ndef windowed_averages(stream, k):\n    # your code here\n    pass\n`,
    expectedOutput: "",
    testCases: [
      { input: "[10, 20, 30, 40], 2", call: "list(windowed_averages([10, 20, 30, 40], 2))", expected: "[15.0, 25.0, 35.0]" },
    ],
  },
  {
    id: 3,
    title: "Suppression and Logging Context Manager",
    slug: "python-suppression-logging-context-manager",
    seoTitle: "Python Advanced Context Managers | Context Managers | Repractiq",
    metaDescription: "Design safe execution context managers that suppress and log selective exception structures.",
    tags: ["context managers", "error handling"],
    difficulty: "Hard",
    type: "function",
    description: "Create a class-based context manager named `SuppressExceptions` that initializes with a list of exception classes. The manager should log any targeted exceptions that occur inside the block to a list attribute called `logged_errors` and suppress them so the program can continue running.",
    explanation: "Context managers use `__enter__` and `__exit__`. Returning `True` from the `__exit__` method signals to Python that the exception was handled and should be suppressed.",
    hint: "Implement `__exit__(self, exc_type, exc_val, exc_tb)`. If `exc_type` matches your exception list, append it to `self.logged_errors` and return `True`.",
    starterCode: `class SuppressExceptions:\n    def __init__(self, *exceptions):\n        self.logged_errors = []\n        # your code here\n\n    def __enter__(self):\n        return self\n\n    def __exit__(self, exc_type, exc_val, exc_tb):\n        # your code here\n        pass\n`,
    expectedOutput: "",
    testCases: [
      { input: "", call: "with SuppressExceptions(ValueError) as se:\n raise ValueError('Oops')\nstr(len(se.logged_errors))", expected: "1" },
    ],
  },
  {
    id: 4,
    title: "Singleton Metaclass Enforcement",
    slug: "python-singleton-metaclass-enforcement",
    seoTitle: "Python Singleton Class Construction | Metaclasses concepts | Repractiq",
    metaDescription: "Learn how to alter class instantiation patterns dynamically across your application using custom Python metaclasses.",
    tags: ["metaclasses concepts", "design patterns"],
    difficulty: "Hard",
    type: "function",
    description: "Write a custom metaclass named `SingletonMeta` that overrides the class creation process to guarantee that only a single instance of a class can ever be instantiated, returning the cached instance on subsequent calls.",
    explanation: "Metaclasses control class structure and instantiation behaviors. Overriding a metaclass's `__call__` method allows you to intercept object instantiation and enforce creational design patterns like the Singleton pattern.",
    hint: "Maintain a private dictionary mapping classes to their unique instances inside the metaclass's allocation structures.",
    starterCode: `class SingletonMeta(type):\n    _instances = {}\n    def __call__(cls, *args, **kwargs):\n        # your code here\n        pass\n`,
    expectedOutput: "",
    testCases: [
      { input: "", call: "class Counter(metaclass=SingletonMeta): pass\nc1 = Counter(); c2 = Counter(); c1 is c2", expected: "True" },
    ],
  },
  {
    id: 5,
    title: "Async Concurrent Task Rate Limiter",
    slug: "python-async-concurrent-task-rate-limiter",
    seoTitle: "Python Asyncio Semaphore Coroutines | Async basics | Repractiq",
    metaDescription: "Throttling microservices and IO tasks safely using async semaphores and concurrent execution pipelines.",
    tags: ["async basics"],
    difficulty: "Hard",
    type: "function",
    description: "Write an asynchronous coroutine called `bounded_gather` that accepts a limit integer `max_concurrent` and a list of awaitable tasks. Run the tasks concurrently while ensuring that no more than `max_concurrent` tasks are executing simultaneously.",
    explanation: "The `asyncio.Semaphore` object helps you limit concurrency. Acquiring a slot inside an async context block allows you to keep tasks waiting until resources become available.",
    hint: "Create an `asyncio.Semaphore(max_concurrent)`. Write a helper worker coroutine that manages the semaphore and awaits individual tasks.",
    starterCode: `import asyncio\n\nasync def bounded_gather(max_concurrent, tasks):\n    # your code here\n    pass\n`,
    expectedOutput: "",
    testCases: [
      { input: "", call: "import inspect; inspect.iscoroutinefunction(bounded_gather)", expected: "True" },
    ],
  },
  {
    id: 6,
    title: "Advanced OOP Custom Property Descriptor",
    slug: "python-advanced-oop-custom-property-descriptor",
    seoTitle: "Python Dynamic Data Descriptors | Advanced OOP | Repractiq",
    metaDescription: "Master property attribution encapsulation using custom data descriptor classes.",
    tags: ["advanced OOP"],
    difficulty: "Hard",
    type: "function",
    description: "Create a data descriptor class called `IntegerRange` that validates assigned attributes. It should enforce that the value is an integer and stays within a minimum and maximum range specified during initialization, raising a `ValueError` for invalid assignments.",
    explanation: "Descriptors give you full control over attribute access by implementing `__get__`, `__set__`, and `__delete__` protocols. This provides a clean way to manage reusable validation logic across multiple classes.",
    hint: "Store the validated data value within the managed object instance dictionary using `instance.__dict__[self.name]` to avoid infinite recursion loops.",
    starterCode: `class IntegerRange:\n    def __init__(self, min_val, max_val):\n        # your code here\n        pass\n\n    def __set_name__(self, owner, name):\n        self.name = name\n\n    def __set__(self, instance, value):\n        # your code here\n        pass\n`,
    expectedOutput: "",
    testCases: [
      { input: "", call: "class Profile:\n age = IntegerRange(18, 99)\np = Profile(); p.age = 25; p.age", expected: "25" },
    ],
  },
  {
    id: 7,
    title: "Observer Pattern Publishing Matrix",
    slug: "python-observer-pattern-publishing-matrix",
    seoTitle: "Python Observer Architecture Design | Design Patterns | Repractiq",
    metaDescription: "Implement event-driven publisher-subscriber design architectures using standard observer patterns.",
    tags: ["design patterns", "advanced OOP"],
    difficulty: "Hard",
    type: "function",
    description: "Design an event system with an `EventManager` class (the Publisher) that allows other objects (Subscribers) to subscribe to specific named event topics. Subscribers must implement an `update(message)` method that runs automatically whenever the event manager fires an event.",
    explanation: "The Observer pattern builds loosely coupled relationships between objects. A central registry matches event strings to sets of listener instances, then notifies them whenever a change occurs.",
    hint: "Maintain a dictionary inside the event manager where keys are event names and values are lists of registered subscriber references.",
    starterCode: `class EventManager:\n    def __init__(self):\n        # your code here\n        pass\n\n    def subscribe(self, event_name, subscriber):\n        pass\n\n    def notify(self, event_name, message):\n        pass\n`,
    expectedOutput: "",
    testCases: [
      { input: "", call: "class Sub: \n def update(self, m): self.m = m\nem = EventManager(); s = Sub(); em.subscribe('err', s); em.notify('err', 'failed'); s.m", expected: "failed" },
    ],
  },
  {
    id: 8,
    title: "Thread-Safe LRU Cache Optimizer",
    slug: "python-thread-safe-lru-cache-optimizer",
    seoTitle: "Python Thread Safe Cache Performance | Performance optimization | Repractiq",
    metaDescription: "Build thread-safe, high-performance least recently used cache structures using mutex structures.",
    tags: ["performance optimization"],
    difficulty: "Hard",
    type: "function",
    description: "Write a class named `ThreadSafeLRUCache` that implements a Least Recently Used (LRU) cache with a fixed maximum capacity. The cache should provide thread-safe `get(key)` and `put(key, value)` methods that execute in $O(1)$ amortized time complexity.",
    explanation: "Combining tracking hash maps with an internal doubly linked list allows you to implement efficient LRU caches. Adding synchronization primitives like `threading.Lock` keeps the structure safe from race conditions during concurrent operations.",
    hint: "Use `collections.OrderedDict` alongside a `threading.Lock` object to handle thread-safe item reordering and evictions.",
    starterCode: `from collections import OrderedDict\nimport threading\n\nclass ThreadSafeLRUCache:\n    def __init__(self, capacity):\n        # your code here\n        pass\n\n    def get(self, key):\n        pass\n\n    def put(self, key, value):\n        pass\n`,
    expectedOutput: "",
    testCases: [
      { input: "2", call: "c = ThreadSafeLRUCache(2); c.put(1, 10); c.put(2, 20); c.get(1)", expected: "10" },
    ],
  },
  {
    id: 9,
    title: "Weakref Lifecycle Graph Tracker",
    slug: "python-weakref-lifecycle-graph-tracker",
    seoTitle: "Python Weak References Architecture | Memory management | Repractiq",
    metaDescription: "Prevent memory leaks in circular data architectures by tracking entity associations using weak references.",
    tags: ["memory management"],
    difficulty: "Hard",
    type: "function",
    description: "Design a cyclic structural model called `Node` that stores links to connected neighbor nodes using weak references (`weakref`). This ensures that complex node cycles do not create reference cycles that prevent the garbage collector from reclaiming memory.",
    explanation: "Standard reference counts prevent objects from being deleted if they point to each other in a loop. Using `weakref.ref` lets you build connections between objects without increasing their reference count, making it easy for the garbage collector to free up memory.",
    hint: "Store references inside a list using `weakref.ref(other_node)`, and retrieve the original object by calling the reference container, checking if it returns `None`.",
    starterCode: `import weakref\n\nclass Node:\n    def __init__(self, name):\n        self.name = name\n        self.neighbors = []\n\n    def add_neighbor(self, node):\n        # your code here\n        pass\n`,
    expectedOutput: "",
    testCases: [
      { input: "", call: "n = Node('A'); n.add_neighbor(n); str(type(n.neighbors[0]))", expected: "<class 'weakref'>" },
    ],
  },
  {
    id: 10,
    title: "Trie Auto-Complete Word Aggregator",
    slug: "python-trie-auto-complete-word-aggregator",
    seoTitle: "Python Trie Prefix Sorting Recursive | Advanced recursion | Repractiq",
    metaDescription: "Solve prefix autocomplete evaluations on highly nested text tree hierarchies using memoized recursion.",
    tags: ["advanced recursion", "nested data structures"],
    difficulty: "Hard",
    type: "class",
    description: "Implement a string prefix search tree (`Trie`) that includes an advanced search method called `get_words_with_prefix(prefix)`. The method should use recursion to find and return a list of all words stored in the tree that begin with that search prefix.",
    explanation: "Trie components store character mappings in nested node layers. Navigating deep down to the prefix node and using recursion to collect all valid downstream word paths makes it easy to build fast, prefix-matching lookup trees.",
    hint: "Find the node matching the prefix string, then run a recursive depth-first search (DFS) traversal function from that node down to find all trailing word markers.",
    starterCode: `class TrieNode:\n    def __init__(self):\n        self.children = {}\n        self.is_word = False\n\nclass Trie:\n    def __init__(self):\n        self.root = TrieNode()\n\n    def insert(self, word):\n        pass\n\n    def get_words_with_prefix(self, prefix):\n        # your code here\n        pass\n`,
    expectedOutput: "",
    testCases: [
      { input: "", call: "t = Trie(); t.insert('car'); t.insert('cat'); sorted(t.get_words_with_prefix('ca'))", expected: "['car', 'cat']" },
    ],
  },
  {
      id: 11,
      title: "Memoization Decorator with Expiration",
      slug: "python-memoization-decorator-expiration",
      seoTitle: "Python Memoize Decorator Time-to-Live | Decorators | Repractiq",
      metaDescription: "Build a stateful function caching decorator featuring an automatic time-based cache expiration policy.",
      tags: ["decorators", "performance optimization"],
      difficulty: "Hard",
      type: "function",
      description: "Write a decorator factory function called `memoize_with_ttl(ttl_seconds)` that caches the return values of a function based on its arguments. If a cached result is older than `ttl_seconds`, it must re-execute the function, update the cache, and return the fresh result.",
      explanation: "Advanced stateful decorators use dictionary collections paired with timestamp assertions. By verifying `time.time() - cached_time < ttl_seconds`, you implement an auto-invalidating cache mechanism.",
      hint: "Store arguments as dictionary keys, mapping them to a tuple containing `(result, insertion_timestamp)`.",
      starterCode: `import functools\nimport time\n\ndef memoize_with_ttl(ttl_seconds):\n    def decorator(func):\n        cache = {}\n        # your code here\n        return func\n    return decorator\n`,
      expectedOutput: "",
      testCases: [
        { input: "60", call: "@memoize_with_ttl(60)\ndef compute(x): return x * 2\ncompute(5)", expected: "10" },
      ],
    },
    {
      id: 12,
      title: "Infinite Prime Number Generator Stream",
      slug: "python-infinite-prime-number-generator-stream",
      seoTitle: "Python Sieve Prime Generator Stream | Generators | Repractiq",
      metaDescription: "Generate prime numbers dynamically via memory-efficient infinite lazy calculation streams.",
      tags: ["generators", "math"],
      difficulty: "Hard",
      type: "function",
      description: "Write a generator function called `prime_generator` that lazily evaluates and yields an infinite sequence of prime numbers sequentially starting from 2.",
      explanation: "Generators excel at processing endless data sequences using $O(1)$ memory footprints. Keeping track of historical primes lets you check subsequent candidates via optimized division validation loops.",
      hint: "Maintain a list of found primes. For every new odd number, confirm it is not divisible by any previously yielded prime up to its square root.",
      starterCode: `def prime_generator():\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "", call: "g = prime_generator(); [next(g) for _ in range(5)]", expected: "[2, 3, 5, 7, 11]" },
      ],
    },
    {
      id: 13,
      title: "Database Transaction Rollback Context Manager",
      slug: "python-database-transaction-rollback-context-manager",
      seoTitle: "Python Database Transaction Rollback Context | Context Managers | Repractiq",
      metaDescription: "Design custom context managers that automatically revert atomic database operations if errors occur.",
      tags: ["context managers", "error handling"],
      difficulty: "Hard",
      type: "function",
      description: "Create a class-based context manager named `Transaction` that accepts a mock database connection object. It should call `conn.begin()`, and upon exit, if no exceptions were raised, commit the state with `conn.commit()`. If any runtime exception occurs, it should call `conn.rollback()` and let the exception propagate.",
      explanation: "Context managers use `__exit__` to examine runtime states. Inspecting the exception type allows the program to safely isolate uncommitted operations and trigger clean rollbacks.",
      hint: "In `__exit__`, check if `exc_type is not None`. If it is, run `self.conn.rollback()` and return `False` so the exception bubbles up.",
      starterCode: `class Transaction:\n    def __init__(self, conn):\n        self.conn = conn\n\n    def __enter__(self):\n        # your code here\n        return self\n\n    def __exit__(self, exc_type, exc_val, exc_tb):\n        # your code here\n        pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "", call: "class MockDb:\n def begin(self): pass\n def commit(self): self.s = 'ok'\n def rollback(self): self.s = 'fail'\ndb = MockDb()\nwith Transaction(db): pass\ndb.s", expected: "ok" },
      ],
    },
    {
      id: 14,
      title: "Abstract Method Enforcer Metaclass",
      slug: "python-abstract-method-enforcer-metaclass",
      seoTitle: "Python Metaclass Interface Enforcement | Metaclasses concepts | Repractiq",
      metaDescription: "Enforce static interface validation behaviors dynamically during module initialization using metaclasses.",
      tags: ["metaclasses concepts", "advanced OOP"],
      difficulty: "Hard",
      type: "function",
      description: "Write a custom metaclass named `InterfaceMeta` that inspects any class using it. If the class specifies a string attribute list named `__required_methods__`, the metaclass must verify that all those named methods are concrete functions on the class, otherwise raising a `TypeError` during class definition.",
      explanation: "Metaclasses process classes at definition time inside `__new__`. Inspecting attributes before allocations are completed lets you enforce explicit interface requirements without relying on standard inheritance.",
      hint: "Inside `__new__(cls, name, bases, dct)`, check if `__required_methods__` exists in `dct`, and loop through them to verify they exist as callable objects inside `dct`.",
      starterCode: `class InterfaceMeta(type):\n    def __new__(cls, name, bases, dct):\n        # your code here\n        return super().__new__(cls, name, bases, dct)\n`,
      expectedOutput: "",
      testCases: [
        { input: "", call: "isinstance(InterfaceMeta, type)", expected: "True" },
      ],
    },
    {
      id: 15,
      title: "Async HTTP Batch Payload Fetcher",
      slug: "python-async-http-batch-payload-fetcher",
      seoTitle: "Python Asyncio Aiohttp Batch Requests | Async basics | Repractiq",
      metaDescription: "Orchestrate asynchronous mass web request routines safely using concurrent task gathers.",
      tags: ["async basics"],
      difficulty: "Hard",
      type: "function",
      description: "Write an asynchronous coroutine called `fetch_all_endpoints` that accepts a list of string URLs and a mock client object that provides an asynchronous `client.get(url)` method. Fetch all endpoints concurrently and return a list of their string response strings.",
      explanation: "Asynchronous task gathering maximizes network execution metrics. Using `asyncio.gather(*tasks)` yields non-blocking scheduling execution for multiple remote IO interactions.",
      hint: "Use a list comprehension to build your tasks list from `client.get(url)`, then call `await asyncio.gather(*tasks)`.",
      starterCode: `import asyncio\n\nasync def fetch_all_endpoints(urls, client):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "", call: "import asyncio; asyncio.iscoroutinefunction(fetch_all_endpoints)", expected: "True" },
      ],
    },
    {
      id: 16,
      title: "Advanced OOP Method Chaining Builder",
      slug: "python-advanced-oop-method-chaining-builder",
      seoTitle: "Python Dynamic Query Fluent API | Advanced OOP | Repractiq",
      metaDescription: "Build a fluent API interface utilizing programmatic method chaining mechanics.",
      tags: ["advanced OOP"],
      difficulty: "Hard",
      type: "function",
      description: "Create a class called `QueryBuilder` that constructs a SQL statement. Implement fluent methods `select(column)`, `where(condition)`, and `limit(val)`. Each method must returns the current object instance (`self`) so calls can be chained together, concluding with a `build()` method that returns the generated query string.",
      explanation: "Method chaining, or the Fluent Interface pattern, requires that mutator class methods explicitly return `self`. This lets you chain multiple analytical steps together in a single sentence layout.",
      hint: "Append query segments to an internal state array inside each method and terminate by returning `self`.",
      starterCode: `class QueryBuilder:\n    def __init__(self):\n        self.query = {}\n\n    def select(self, col):\n        # your code here\n        pass\n\n    def build(self):\n        # your code here\n        pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "", call: "QueryBuilder().select('id').build()", expected: "SELECT id" },
      ],
    },
    {
      id: 17,
      title: "Behavioral State Pattern Traffic Controller",
      slug: "python-behavioral-state-pattern-traffic-controller",
      seoTitle: "Python State Pattern Machine | Design Patterns | Repractiq",
      metaDescription: "Encapsulate contextual behaviors within distinct class configurations using behavioral state patterns.",
      tags: ["design patterns", "advanced OOP"],
      difficulty: "Hard",
      type: "function",
      description: "Design a traffic signal state engine using the State Pattern. Create a context controller `TrafficLight` that delegates its `request()` action to a decoupled internal state class instance (`RedState`, `GreenState`, `YellowState`), transitioning state references automatically on each invocation.",
      explanation: "The State pattern extracts state-specific behavior into individual classes. Instead of relying on monolithic conditional statements, the context object switches its internal state reference to change its behavior dynamically.",
      hint: "Let each state class define a `handle(self, context)` method that alters `context.state` to the next phase.",
      starterCode: `class TrafficLight:\n    def __init__(self, initial_state):\n        self.state = initial_state\n\n    def change_state(self, state):\n        self.state = state\n\n    def request(self):\n        # your code here\n        pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "", call: "hasattr(TrafficLight, 'request')", expected: "True" },
      ],
    },
    {
      id: 18,
      title: "Matrix Multiplication Slot Matrix Vectorizer",
      slug: "python-matrix-multiplication-slot-matrix-vectorizer",
      seoTitle: "Python Vectorized Dot Product Loops | Performance optimization | Repractiq",
      metaDescription: "Optimize multi-dimensional matrix multiplications using structured index caching routines.",
      tags: ["performance optimization", "math"],
      difficulty: "Hard",
      type: "function",
      description: "Write a function called `optimized_matrix_multiply` that performs classic row-column multiplication over two 2D matrices using flat indexed array strategies to minimize lookups, optimizing execution time over nested array components.",
      explanation: "Nested index lookups introduce overhead in CPU execution loops. Restructuring iterative row loops to take advantage of contiguous element access improves memory lookup speeds.",
      hint: "Transpose the second matrix first to turn column lookups into linear row lookups, maximizing memory cache performance.",
      starterCode: `def optimized_matrix_multiply(matrix_a, matrix_b):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "[[1, 2]], [[3], [4]]", call: "str(optimized_matrix_multiply([[1, 2]], [[3], [4]]))", expected: "[[11]]" },
      ],
    },
    {
      id: 19,
      title: "Custom Garbage Collection Reference Pool",
      slug: "python-custom-garbage-collection-reference-pool",
      seoTitle: "Python Sys Getrefcount Cleanup | Memory management | Repractiq",
      metaDescription: "Monitor low-level memory reference structures dynamically using tracking modules.",
      tags: ["memory management"],
      difficulty: "Hard",
      type: "function",
      description: "Write a function called `check_reference_leak` that takes an object reference and an execution tracking handler. Use the `sys` module to measure variations in reference count values before and after executing the handler, verifying whether memory allocations were completely cleaned up.",
      explanation: "Tracking reference count fluctuations with `sys.getrefcount()` allows you to identify hidden memory leaks, such as global array accumulation or unresolved variable references.",
      hint: "Subtract the starting reference count from the final count, taking into account the extra reference created by passing the object to `getrefcount` itself.",
      starterCode: `import sys\n\ndef check_reference_leak(obj, handler):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "[], lambda x: x", call: "str(type(check_reference_leak([], lambda x: x)))", expected: "<class 'int'>" },
      ],
    },
    {
      id: 20,
      title: "Recursive Flattening of Arbitrary Structures",
      slug: "python-recursive-flattening-arbitrary-structures",
      seoTitle: "Python Nested Dictionary Recursion | Advanced recursion | Repractiq",
      metaDescription: "Flatten highly complex, multi-tiered JSON dictionary hierarchies into simple key-value pairs using recursion.",
      tags: ["advanced recursion", "nested data structures"],
      difficulty: "Hard",
      type: "function",
      description: "Write a recursive function called `flatten_dict` that flattens a deeply nested dictionary structure. Keys should be combined using a period (`.`) separator, turning `{ 'a': { 'b': 1 } }` into `{ 'a.b': 1 }`.",
      explanation: "Deeply nested tree data cannot be parsed using simple loops. A recursive solution checks the data types at each level, joining path strings together until it reaches a primitive value.",
      hint: "Loop through keys and values. If a value is a dictionary, make a recursive call with a updated path string prefix.",
      starterCode: `def flatten_dict(d, parent_key='', sep='.'):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "{'a': {'b': 5}}", call: "str(flatten_dict({'a': {'b': 5}}))", expected: "{'a.b': 5}" },
      ],
    },
    {
      id: 21,
      title: "Access Control Authorization Decorator",
      slug: "python-access-control-authorization-decorator",
      seoTitle: "Python Role Checking Decorator | Decorators | Repractiq",
      metaDescription: "Enforce user role access control validations securely using parameterized decorators.",
      tags: ["decorators"],
      difficulty: "Hard",
      type: "function",
      description: "Write a decorator called `require_roles(*allowed_roles)` that inspects a keyword argument `user_role` on the decorated function. If the role passed matches one of the allowed roles, execute the function; otherwise, raise a `PermissionError`.",
      explanation: "Decorators are great for cross-cutting concerns like security. Inspecting `kwargs` lets you run validation checks before allowing the wrapped code to execute.",
      hint: "Extract `user_role` out of `kwargs` or `args` indices inside the wrapper function, and validate it against the `allowed_roles` collection.",
      starterCode: `import functools\n\ndef require_roles(*allowed_roles):\n    def decorator(func):\n        # your code here\n        return func\n    return decorator\n`,
      expectedOutput: "",
      testCases: [
        { input: "", call: "@require_roles('admin')\ndef core(user_role): return 'secret'\ncore(user_role='admin')", expected: "secret" },
      ],
    },
    {
      id: 22,
      title: "Bidirectional Coroutine Generator Pipeline",
      slug: "python-bidirectional-coroutine-generator-pipeline",
      seoTitle: "Python Generator Send Consumer Pipeline | Generators | Repractiq",
      metaDescription: "Implement responsive two-way data streaming pipelines using generator send protocols.",
      tags: ["generators"],
      difficulty: "Hard",
      type: "function",
      description: "Write a generator function called `running_variance` acting as a consumer coroutine. It should accept input data using the `.send(value)` protocol, tracking and yielding the running statistical variance of all values received so far.",
      explanation: "Using `yield` as an expression allows generators to receive values dynamically via `.send()`. This makes them highly useful for implementing real-time event consumers and responsive data processing pipelines.",
      hint: "Prime the coroutine by calling `next(gen)` or initializing it, then use a `while True:` loop containing `val = yield output`.",
      starterCode: `def running_variance():\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "", call: "g = running_variance(); next(g); str(type(g.send(10)))", expected: "<class 'float'>" },
      ],
    },
    {
      id: 23,
      title: "Temporary Working Directory Context Manager",
      slug: "python-temporary-working-directory-context-manager",
      seoTitle: "Python Change Directory Safely Context | Context Managers | Repractiq",
      metaDescription: "Safely isolate filesystem interactions using directory-swapping context routines.",
      tags: ["context managers", "file handling concepts"],
      difficulty: "Hard",
      type: "function",
      description: "Create a context manager called `WorkingDir` that changes the current working directory to a specified path upon entry, and reliably switches back to the original directory when exiting, even if errors occurred within the block.",
      explanation: "Combining `os.getcwd()` with `os.chdir()` allows you to alter environment paths temporarily. Wrapping the teardown steps inside the exit sequence ensures the workspace is restored safely.",
      hint: "Save `os.getcwd()` in `__enter__`, switch to the target path, and run `os.chdir(saved_path)` inside `__exit__`.",
      starterCode: `import os\n\nclass WorkingDir:\n    def __init__(self, target_path):\n        # your code here\n        pass\n\n    def __enter__(self):\n        pass\n\n    def __exit__(self, exc_type, exc_val, exc_tb):\n        pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "", call: "hasattr(WorkingDir, '__enter__') and hasattr(WorkingDir, '__exit__')", expected: "True" },
      ],
    },
    {
      id: 24,
      title: "Automatic Property Generator Metaclass",
      slug: "python-automatic-property-generator-metaclass",
      seoTitle: "Python Metaclass Getter Setter Injection | Metaclasses concepts | Repractiq",
      metaDescription: "Inject encapsulated getter and setter properties automatically during runtime using class compilation overrides.",
      tags: ["metaclasses concepts", "advanced OOP"],
      difficulty: "Hard",
      type: "function",
      description: "Create a metaclass called `AutoProps` that transforms plain string fields prefixed with an underscore (e.g., `_age`) into formal Python properties complete with automatic getter and setter attributes.",
      explanation: "Metaclasses can dynamically modify class dictionaries before creation. By replacing raw attributes with managed properties, you can eliminate repetitive boilerplate validation code.",
      hint: "Loop over keys in the class dictionary, detect matching attribute strings, and bind `property()` instances into the class definition dict.",
      starterCode: `class AutoProps(type):\n    def __new__(cls, name, bases, dct):\n        # your code here\n        return super().__new__(cls, name, bases, dct)\n`,
      expectedOutput: "",
      testCases: [
        { input: "", call: "isinstance(AutoProps, type)", expected: "True" },
      ],
    },
    {
      id: 25,
      title: "Async Resource Pool Concurrency Manager",
      slug: "python-async-resource-pool-concurrency-manager",
      seoTitle: "Python Asyncio Queue Worker Multiplexing | Async basics | Repractiq",
      metaDescription: "Distribute workloads safely across concurrent worker clusters using async queue components.",
      tags: ["async basics"],
      difficulty: "Hard",
      type: "function",
      description: "Write an asynchronous orchestrator called `process_pool_jobs` that uses an `asyncio.Queue` to distribute a collection of jobs across a fixed group of concurrent background worker tasks, returning when all tasks are complete.",
      explanation: "Asynchronous queues provide thread-safe coordination for coroutines. Workers can pull items from the queue, process them concurrently, and signal completion using `queue.task_done()`.",
      hint: "Push jobs into the queue, start worker loops using `asyncio.create_task`, and use `await queue.join()` to wait until everything is processed.",
      starterCode: `import asyncio\n\nasync def process_pool_jobs(job_list, worker_count):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "", call: "import asyncio; asyncio.iscoroutinefunction(process_pool_jobs)", expected: "True" },
      ],
    },
    {
      id: 26,
      title: "Dynamic Class Factory Extender",
      slug: "python-dynamic-class-factory-extender",
      seoTitle: "Python Type Dynamic Class Creation | Advanced OOP | Repractiq",
      metaDescription: "Generate fully functional classes programmatically at runtime using type construction strategies.",
      tags: ["advanced OOP"],
      difficulty: "Hard",
      type: "function",
      description: "Write a function called `create_model_class(class_name, field_specs)` that generates a new Python class dynamically using the built-in `type()` function, complete with an `__init__` constructor based on the field specifications.",
      explanation: "The three-argument version of `type(name, bases, dict)` allows you to build classes programmatically at runtime. This provides a clean way to handle dynamic schemas, such as parsing databases or external JSON structures.",
      hint: "Define a dynamic initializer function `def init(self, **kwargs): ...` and pass it into the attributes dictionary of the `type()` call.",
      starterCode: `def create_model_class(class_name, field_specs):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "'User', ['id']", call: "C = create_model_class('User', ['id']); obj = C(id=1); obj.id", expected: "1" },
      ],
    },
    {
      id: 27,
      title: "Structural Command Pattern Invoker",
      slug: "python-structural-command-pattern-invoker",
      seoTitle: "Python Command Pattern Undo Redo | Design Patterns | Repractiq",
      metaDescription: "Build undoable application workflows using structured object encapsulation models.",
      tags: ["design patterns", "advanced OOP"],
      difficulty: "Hard",
      type: "function",
      description: "Design an undoable command tracking engine. Create an abstract `Command` interface along with a concrete `UpdateTextCommand` class, managed by a tracking history stack class that can execute, undo, and redo adjustments smoothly.",
      explanation: "The Command pattern turns actions into standalone objects. Storing these command objects in a historical array structure makes it easy to add robust undo/redo functionality to your application.",
      hint: "Keep two internal arrays, `undo_stack` and `redo_stack`, to track your command execution state changes.",
      starterCode: `class Command:\n    def execute(self): pass\n    def undo(self): pass\n\nclass CommandHistory:\n    def __init__(self):\n        # your code here\n        pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "", call: "hasattr(Command, 'execute') and hasattr(Command, 'undo')", expected: "True" },
      ],
    },
    {
      id: 28,
      title: "String Concatenation Memory Optimizer",
      slug: "python-string-concatenation-memory-optimizer",
      seoTitle: "Python String Join Performance Optimization | Performance optimization | Repractiq",
      metaDescription: "Optimize massive text merging routines using buffer arrays instead of iterative operator chains.",
      tags: ["performance optimization", "string manipulation"],
      difficulty: "Hard",
      type: "function",
      description: "Write a function called `efficient_log_merge` that aggregates millions of short log strings. Avoid standard iterative loop additions (`+=`), demonstrating instead how to maximize performance using memory-efficient data structures.",
      explanation: "Strings are immutable in Python. Using `+=` in a loop repeatedly copies data in memory, leading to $O(N^2)$ complexity. Collecting strings in a list and merging them with `''.join()` reduces this to efficient linear time ($O(N)$).",
      hint: "Collect all text segments into an intermediate array buffer, and run `''.join(buffer)` to merge them efficiently.",
      starterCode: `def efficient_log_merge(log_fragments):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "['a', 'b']", call: "efficient_log_merge(['a', 'b'])", expected: "ab" },
      ],
    },
    {
      id: 29,
      title: "Slot Optimization Allocation Tracker",
      slug: "python-slot-optimization-allocation-tracker",
      seoTitle: "Python Slots Attribute Memory Footprint | Memory management | Repractiq",
      metaDescription: "Optimize memory layouts for instances by eliminating dynamic attribute dictionary tracking.",
      tags: ["memory management", "advanced OOP"],
      difficulty: "Hard",
      type: "function",
      description: "Create a class called `OptimizedCoordinate` that defines `__slots__` for `x`, `y`, and `z` attributes. Write a test verification script that confirms the instance prevents arbitrary dictionary overrides, keeping its memory footprint low.",
      explanation: "By default, Python objects use a dynamic dictionary (`__dict__`) to store attributes, which adds significant memory overhead. Defining `__slots__` tells Python to use a highly efficient, fixed-size array instead, saving memory when creating millions of instances.",
      hint: "Declare `__slots__ = ('x', 'y', 'z')` directly inside the class body definition.",
      starterCode: `class OptimizedCoordinate:\n    __slots__ = ('x', 'y', 'z')\n    def __init__(self, x, y, z):\n        # your code here\n        pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "", call: "hasattr(OptimizedCoordinate(1,2,3), '__dict__')", expected: "False" },
      ],
    },
    {
      id: 30,
      title: "Recursive Graph Cycle Detector",
      slug: "python-recursive-graph-cycle-detector",
      seoTitle: "Python Recursive DFS Cycle Detection | Advanced recursion | Repractiq",
      metaDescription: "Detect cyclic dependencies in graph architectures using recursive backtracking algorithms.",
      tags: ["advanced recursion"],
      difficulty: "Hard",
      type: "function",
      description: "Write a function called `has_cycle` that takes a directed graph represented as an adjacency list dictionary. Use recursive Depth-First Search (DFS) with backtracking states to determine if the graph contains any cyclic paths.",
      explanation: "Finding cycles in a directed graph requires tracking the nodes currently in the recursion stack. If a recursive step encounters a node that is already active in the current path, a cycle has been found.",
      hint: "Maintain two tracking sets during your recursive traversal: a `visited` set for fully processed nodes, and a `rec_stack` set for nodes currently in the active path.",
      starterCode: `def has_cycle(graph):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "{0: [1], 1: [0]}", call: "has_cycle({0: [1], 1: [0]})", expected: "True" },
      ],
    },
    {
      id: 31,
      title: "Execution Time Profiler Decorator",
      slug: "python-execution-time-profiler-decorator",
      seoTitle: "Python Function Timer Profiling | Decorators | Repractiq",
      metaDescription: "Build performance measuring tools using execution wrapper profiling structures.",
      tags: ["decorators", "performance optimization"],
      difficulty: "Hard",
      type: "function",
      description: "Write a decorator called `profile_time(stats_container)` that measures how long a function takes to execute. The elapsed float time in seconds must be appended to the provided `stats_container` list attribute every time the function runs.",
      explanation: "Decorators are ideal for performance profiling. Using high-resolution timers around the core function execution lets you monitor application speeds without cluttering your business logic.",
      hint: "Record the start time using `time.perf_counter()`, call the original function, then measure the difference and add it to `stats_container`.",
      starterCode: `import functools\nimport time\n\ndef profile_time(stats_container):\n    def decorator(func):\n        # your code here\n        return func\n    return decorator\n`,
      expectedOutput: "",
      testCases: [
        { input: "", call: "s = []; @profile_time(s)\ndef run(): return 1\nrun(); str(len(s))", expected: "1" },
      ],
    },
    {
      id: 32,
      title: "Lazy Flattening Generator for Nested Iterables",
      slug: "python-lazy-flattening-generator-nested-iterables",
      seoTitle: "Python Generator Flatten Nested Tree | Generators | Repractiq",
      metaDescription: "Flatten highly irregular, deeply nested array matrices using lazy generator yields.",
      tags: ["generators", "nested data structures"],
      difficulty: "Hard",
      type: "function",
      description: "Write a generator function called `deep_flatten` that accepts a deeply nested iterable structure and yields individual leaf items one by one in a flat, linear stream.",
      explanation: "Parsing deeply nested trees can easily consume too much memory if you flatten them into lists all at once. Using `yield from` recursively lets you flatten irregular structures lazily with minimal memory overhead.",
      hint: "Iterate through elements; if an item is a sub-iterable (excluding strings), execute `yield from deep_flatten(item)`, otherwise simply `yield item`.",
      starterCode: `def deep_flatten(nested_iterable):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "[[1, [2]], 3]", call: "list(deep_flatten([[1, [2]], 3]))", expected: "[1, 2, 3]" },
      ],
    },
    {
      id: 33,
      title: "Safe File Lock Context Manager",
      slug: "python-safe-file-lock-context-manager",
      seoTitle: "Python Process File Locking Lock | Context Managers | Repractiq",
      metaDescription: "Prevent shared resource file corruption by building exclusive file-locking context components.",
      tags: ["context managers", "file handling concepts"],
      difficulty: "Hard",
      type: "function",
      description: "Write a context manager called `FileLock` that creates an exclusive lock file (e.g., `filename.lock`) upon entry to prevent other processes from accessing the resource, and cleanly deletes the lock file when exiting.",
      explanation: "File locks ensure that concurrent processes don't step on each other's data. Managing the creation and cleanup of these lock tokens inside an `__enter__` and `__exit__` context block prevents deadlocks and file corruption.",
      hint: "Use `os.open` with exclusive flags `os.O_CREAT | os.O_EXCL` during entry, and remove the file using `os.remove` when exiting.",
      starterCode: `import os\n\nclass FileLock:\n    def __init__(self, filepath):\n        self.lockfile = filepath + ".lock"\n\n    def __enter__(self):\n        # your code here\n        return self\n\n    def __exit__(self, exc_type, exc_val, exc_tb):\n        # your code here\n        pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "", call: "hasattr(FileLock, '__enter__')", expected: "True" },
      ],
    },
    {
      id: 34,
      title: "Strict Type Validation Metaclass",
      slug: "python-strict-type-validation-metaclass",
      seoTitle: "Python Metaclass Type Guard Enforcement | Metaclasses concepts | Repractiq",
      metaDescription: "Enforce strict type validation rules for initialized attributes using custom metaclasses.",
      tags: ["metaclasses concepts", "error handling"],
      difficulty: "Hard",
      type: "function",
      description: "Create a metaclass called `StrictTypeMeta` that reads an structural `__annotations__` dictionary map on the class definitions, generating an initialization routine that automatically raises a `TypeError` if any provided argument type mismatches.",
      explanation: "Metaclasses can inspect type hints to enforce validation automatically. Overriding how instances are initialized allows you to build strict type-checking mechanisms directly into your domain models.",
      hint: "Intercept class initialization by wrapping or overriding the target `__init__` method, checking passed parameters against `self.__annotations__`.",
      starterCode: `class StrictTypeMeta(type):\n    def __new__(cls, name, bases, dct):\n        # your code here\n        return super().__new__(cls, name, bases, dct)\n`,
      expectedOutput: "",
      testCases: [
        { input: "", call: "isinstance(StrictTypeMeta, type)", expected: "True" },
      ],
    },
    {
      id: 35,
      title: "Async Microservice Event Broadcaster",
      slug: "python-async-microservice-event-broadcaster",
      seoTitle: "Python Asyncio PubSub Event Queue | Async basics | Repractiq",
      metaDescription: "Build highly scalable, non-blocking asynchronous event publishers using asyncio.",
      tags: ["async basics", "design patterns"],
      difficulty: "Hard",
      type: "function",
      description: "Write an asynchronous event engine class named `AsyncHub` that allows multiple consumer coroutines to listen to a central message channel using individual `asyncio.Queue` pipes, routing messages to all active channels concurrently.",
      explanation: "Building a publish-subscribe system in async Python relies on duplicating event distribution. Storing client queue streams in a central registry allows a single broadcast call to deliver updates to all listeners without blocking.",
      hint: "Maintain a set of `asyncio.Queue` subscriber instances, and use a loop to push messages into every queue during a broadcast.",
      starterCode: `import asyncio\n\nclass AsyncHub:\n    def __init__(self):\n        self.listeners = set()\n\n    def subscribe(self):\n        # your code here\n        pass\n\n    async def broadcast(self, msg):\n        # your code here\n        pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "", call: "import inspect; inspect.isisinstance = isinstance(AsyncHub(), AsyncHub)", expected: "True" },
      ],
    },
    {
      id: 36,
      title: "Advanced OOP Virtual Method Slot Resolution",
      slug: "python-advanced-oop-virtual-method-slot-resolution",
      seoTitle: "Python MRO Overriding Resolution | Advanced OOP | Repractiq",
      metaDescription: "Master complex multiple inheritance paths by analyzing Method Resolution Order (MRO) mechanics.",
      tags: ["advanced OOP"],
      difficulty: "Hard",
      type: "function",
      description: "Design a complex diamond inheritance hierarchy featuring classes `A`, `B(A)`, `C(A)`, and `D(B, C)`. Add an inspection method to class `D` that returns the exact Method Resolution Order (MRO) tuple path using `super()` dispatching logic.",
      explanation: "Python resolves multiple inheritance structures using the C3 Linearization algorithm. Understanding how `super()` paths cross sibling classes is essential for preventing initialization bugs in large, complex systems.",
      hint: "Call `D.__mro__` or use `self.__class__.mro()` to see the calculated resolution tree path.",
      starterCode: `class A:\n    def show(self): return "A"\nclass B(A):\n    def show(self): return "B" + super().show()\nclass C(A):\n    def show(self): return "C" + super().show()\nclass D(B, C):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "", call: "D().show()", expected: "BCA" },
      ],
    },
    {
      id: 37,
      title: "Structural Flyweight Pattern Factory",
      slug: "python-structural-flyweight-pattern-factory",
      seoTitle: "Python Flyweight Object Sharing | Design Patterns | Repractiq",
      metaDescription: "Minimize application memory overhead by caching and sharing immutable object instances using the Flyweight pattern.",
      tags: ["design patterns", "memory management"],
      difficulty: "Hard",
      type: "function",
      description: "Implement a Flyweight pattern architecture for a gaming engine. Create a shared `GlyphFlyweight` factory that caches and shares immutable object textures, ensuring that creating thousands of duplicate entities does not allocate new redundant objects.",
      explanation: "The Flyweight pattern minimizes memory use by sharing as much data as possible with related objects. Storing immutable intrinsic states in a central factory lookup map prevents unnecessary object allocations.",
      hint: "Use a factory dictionary where keys represent unique state properties, returning existing instances whenever a match is found.",
      starterCode: `class GlyphFlyweight:\n    def __init__(self, shared_state):\n        self.shared_state = shared_state\n\nclass FlyweightFactory:\n    _pool = {}\n    @classmethod\n    def get_flyweight(cls, shared_state):\n        # your code here\n        pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "'tree'", call: "FlyweightFactory.get_flyweight('tree') is FlyweightFactory.get_flyweight('tree')", expected: "True" },
      ],
    },
    {
      id: 38,
      title: "Pre-Allocated Array Buffer Optimizer",
      slug: "python-pre-allocated-array-buffer-optimizer",
      seoTitle: "Python Bytearray Memory Caching Buffer | Performance optimization | Repractiq",
      metaDescription: "Avoid garbage collection thrashing in high-throughput applications by reusing pre-allocated data streams.",
      tags: ["performance optimization", "memory management"],
      difficulty: "Hard",
      type: "function",
      description: "Write a high-performance buffer manager class called `BufferPool` that allocates a fixed number of reusable static `bytearray` objects, eliminating runtime allocations and reducing garbage collection overhead during heavy IO streaming operations.",
      explanation: "Frequent object creation and destruction triggers Python's garbage collector, which can degrade application performance. Reusing a pool of pre-allocated buffers keeps memory usage stable and throughput high.",
      hint: "Maintain an internal tracking array of free `bytearray` slices, serving them via `checkout()` and reclaiming them via `checkin()`.",
      starterCode: `class BufferPool:\n    def __init__(self, size, count):\n        self.pool = [bytearray(size) for _ in range(count)]\n        # your code here\n`,
      expectedOutput: "",
      testCases: [
        { input: "", call: "str(type(BufferPool(10, 2).pool[0]))", expected: "<class 'bytearray'>" },
      ],
    },
    {
      id: 39,
      title: "Circular Reference Graph Purger",
      slug: "python-circular-reference-graph-purger",
      seoTitle: "Python Manual Garbage Collection Cycle | Memory management | Repractiq",
      metaDescription: "Force the isolation and deletion of complex circular reference loops using manual garbage collection adjustments.",
      tags: ["memory management"],
      difficulty: "Hard",
      type: "function",
      description: "Write a memory analysis function called `force_cycle_cleanup` that builds an isolated circular reference loop between objects, disables automatic garbage collection using `gc`, and performs a manual collection, returning the integer count of unreachable objects cleared.",
      explanation: "Circular references cannot be cleared by simple reference counting alone; they require Python's cyclic garbage collector. Managing this process manually helps you verify exactly when cyclic structures are freed from memory.",
      hint: "Import `gc`, disable automatic collection with `gc.disable()`, break your references or delete local pointers, then run and return `gc.collect()`.",
      starterCode: `import gc\n\ndef force_cycle_cleanup():\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "", call: "str(type(force_cycle_cleanup()))", expected: "<class 'int'>" },
      ],
    },
    {
      id: 40,
      title: "Recursive Mathematical Expression Parser",
      slug: "python-recursive-mathematical-expression-parser",
      seoTitle: "Python Recursive Descent Parser Tree | Advanced recursion | Repractiq",
      metaDescription: "Evaluate complex mathematical equations containing nested parentheses using a recursive descent parser.",
      tags: ["advanced recursion"],
      difficulty: "Hard",
      type: "function",
      description: "Write a recursive descent parsing function called `evaluate_expression` that accepts a string mathematical statement containing digits, basic operators (`+`, `-`), and nested parentheses `()`, returning the correct integer result.",
      explanation: "Nested expressions cannot be evaluated using simple regex tools. A recursive descent parser processes text tokens sequentially, branching into a new recursive execution level whenever it encounters an opening parenthesis.",
      hint: "Track your character reading index globally or pass it through your functions, recursively calling your expression evaluator whenever an opening parenthesis `(` is found.",
      starterCode: `def evaluate_expression(expr):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "'1+(3-2)'", call: "evaluate_expression('1+(3-2)')", expected: "2" },
      ],
    },
    {
      id: 41,
      title: "Class Decorator for Method Logging",
      slug: "python-class-decorator-method-logging",
      seoTitle: "Python Class Decorator Method Interception | Decorators | Repractiq",
      metaDescription: "Learn to build advanced class decorators that programmatically intercept and log every internal method invocation.",
      tags: ["decorators", "advanced OOP"],
      difficulty: "Hard",
      type: "function",
      description: "Write a class decorator named `log_all_methods` that wraps every callable method in a class. When any method of the decorated class is called, it should append a tuple string containing `(method_name, status)` ('success' or 'error') to a global list named `execution_log`.",
      explanation: "Class decorators inspect and modify class attributes before construction concludes. Iterating over the class dictionary allows you to wrap functions with interception logic dynamically.",
      hint: "Use `inspect.isroutine` or loop over `dir(cls)` to find methods, wrapping them in a standard execution-logging wrapper.",
      starterCode: `execution_log = []\n\ndef log_all_methods(cls):\n    # your code here\n    return cls\n`,
      expectedOutput: "",
      testCases: [
        { input: "", call: "@log_all_methods\nclass A:\n def test(self): return 1\nA().test(); str(execution_log)", expected: "[('test', 'success')]" },
      ],
    },
    {
      id: 42,
      title: "Cooperative Multitasking Coroutine Scheduler",
      slug: "python-cooperative-multitasking-coroutine-scheduler",
      seoTitle: "Python Generator Yield Coroutine Scheduler | Generators | Repractiq",
      metaDescription: "Build a minimal operational task loop using generators to replicate cooperative async mechanics.",
      tags: ["generators"],
      difficulty: "Hard",
      type: "function",
      description: "Write a simple loop controller called `TaskScheduler` that accepts a list of generators. It should execute them round-robin fashion by calling `next()` on each, discarding finished generators, until all are exhausted.",
      explanation: "Before async/await, generators served as the foundation for async runtimes. Using yield pauses execution, yielding control back to a central scheduler loop to run another task.",
      hint: "Use a `collections.deque` containing active generators. Pop one out, run `next()`, and append it back if it doesn't raise `StopIteration`.",
      starterCode: `from collections import deque\n\ndef run_scheduler(tasks):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "", call: "def t1(): yield 1\ndef t2(): yield 2\nrun_scheduler([t1(), t2()])", expected: "None" },
      ],
    },
    {
      id: 43,
      title: "Resource Profiling Context Manager",
      slug: "python-resource-profiling-context-manager",
      seoTitle: "Python Memory Profiler Context Manager | Context Managers | Repractiq",
      metaDescription: "Design a diagnostic execution context manager that tracks delta memory modifications.",
      tags: ["context managers", "memory management"],
      difficulty: "Hard",
      type: "function",
      description: "Create a context manager using the `@contextmanager` decorator named `profile_memory` that records the process memory use before and after executing a block, appending the calculated float byte difference to a provided monitoring list.",
      explanation: "The `@contextmanager` decorator lets you build functional context blocks using a single `yield`. Any setup runs before the yield, while teardown and measurement run in a try-finally block after it.",
      hint: "Use `os` or a mock tracker to capture memory states. Put the `yield` statement inside a `try...finally` block.",
      starterCode: `from shortcuts import get_memory_usage\nfrom contextlib import contextmanager\n\n@contextmanager\ndef profile_memory(log_target):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "", call: "l = [];\nwith profile_memory(l): pass\nisinstance(l, list)", expected: "True" },
      ],
    },
    {
      id: 44,
      title: "Registry Metaclass Plugin Architecture",
      slug: "python-registry-metaclass-plugin-architecture",
      seoTitle: "Python Metaclass Subclass Plugin Registry | Metaclasses concepts | Repractiq",
      metaDescription: "Discover how to track and register application module extensions automatically using custom metaclasses.",
      tags: ["metaclasses concepts", "design patterns"],
      difficulty: "Hard",
      type: "function",
      description: "Write a metaclass called `PluginRegistryMeta` that maintains a central dictionary named `registry`. Any subclass that inherits from a class using this metaclass must automatically add its name and class reference to this registry.",
      explanation: "Metaclasses make building plugin architectures easy. By intercepting class creation, you can register new modules automatically without manual configuration scripts.",
      hint: "Initialize a class variable dictionary `registry` on the metaclass, and append references during the `__init__` or `__new__` phase.",
      starterCode: `class PluginRegistryMeta(type):\n    registry = {}\n    def __init__(cls, name, bases, dct):\n        # your code here\n        super().__init__(name, bases, dct)\n`,
      expectedOutput: "",
      testCases: [
        { input: "", call: "class Base(metaclass=PluginRegistryMeta): pass\nclass Hook(Base): pass\n'Hook' in PluginRegistryMeta.registry", expected: "True" },
      ],
    },
    {
      id: 45,
      title: "Async Parallel Resource Downloader",
      slug: "python-async-parallel-resource-downloader",
      seoTitle: "Python Asyncio AsCompleted Processing | Async basics | Repractiq",
      metaDescription: "Process incoming concurrent streaming payloads in real time as they finish using asyncio.",
      tags: ["async basics"],
      difficulty: "Hard",
      type: "function",
      description: "Write an asynchronous coroutine called `process_as_completed` that takes a list of awaitable tasks. Iterate over them as they complete using `asyncio.as_completed`, passing each result immediately to a synchronous `log_result` function.",
      explanation: "`asyncio.as_completed` returns an iterator that yields futures in the order they finish. This allows you to process completed network payloads instantly, rather than waiting for the entire batch to sync.",
      hint: "Use a `for future in asyncio.as_completed(tasks):` loop, await the individual future, and process the results.",
      starterCode: `import asyncio\n\nasync def process_as_completed(tasks, log_result):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "", call: "import asyncio; asyncio.iscoroutinefunction(process_as_completed)", expected: "True" },
      ],
    },
    {
      id: 46,
      title: "Dynamic Method Dispatcher Multi-Method",
      slug: "python-dynamic-method-dispatcher-multi-method",
      seoTitle: "Python Multiple Dispatch Signature Routing | Advanced OOP | Repractiq",
      metaDescription: "Route method calls to different functions at runtime based on the argument type signatures.",
      tags: ["advanced OOP"],
      difficulty: "Hard",
      type: "function",
      description: "Implement a custom multi-method dispatcher function called `multimethod`. It should act as a decorator that registers multiple versions of the same function name, executing the correct version at runtime based on the argument types.",
      explanation: "Python doesn't support method overloading by default. Building an explicit signature map dictionary allows you to choose and execute the right function variation depending on the types passed at runtime.",
      hint: "Maintain a registry mapping a tuple of parameter types to their target functions inside the decorator wrapper closure.",
      starterCode: `def multimethod(*types):\n    def decorator(func):\n        # your code here\n        return func\n    return decorator\n`,
      expectedOutput: "",
      testCases: [
        { input: "", call: "hasattr(multimethod, '__call__')", expected: "True" },
      ],
    },
    {
      id: 47,
      title: "Abstract Factory Strategy Pattern Matrix",
      slug: "python-abstract-factory-strategy-pattern-matrix",
      seoTitle: "Python Abstract Factory Component Generation | Design Patterns | Repractiq",
      metaDescription: "Design highly decoupled, swappable structural component ecosystems using Abstract Factories.",
      tags: ["design patterns", "advanced OOP"],
      difficulty: "Hard",
      type: "function",
      description: "Create an Abstract Factory architectural blueprint. Build an interface called `UIFactory` along with concrete classes `DarkFactory` and `LightFactory` that return matching, theme-specific structural widgets like Buttons and Scrollbars.",
      explanation: "The Abstract Factory pattern provides an interface for creating families of related objects without specifying their concrete classes. This keeps your user-interface code decoupled from specific styling implementations.",
      hint: "Define clean operational interfaces with methods like `create_button()` returning decoupled structural objects.",
      starterCode: `class UIFactory:\n    def create_button(self): pass\n\nclass DarkFactory(UIFactory):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "", call: "subclasscheck = issubclass(DarkFactory, UIFactory)", expected: "True" },
      ],
    },
    {
      id: 48,
      title: "High-Performance Slots Data Frame Mixer",
      slug: "python-high-performance-slots-data-frame-mixer",
      seoTitle: "Python Memory Profiling Objects Slots | Performance optimization | Repractiq",
      metaDescription: "Build memory-optimized row object sets for large data pipelines by utilizing fixed slot definitions.",
      tags: ["performance optimization", "memory management"],
      difficulty: "Hard",
      type: "function",
      description: "Write a high-capacity custom data parsing loop that takes a list of dictionaries and converts them into instances of a class that uses `__slots__`. Measure the time and memory saved compared to standard dynamic objects.",
      explanation: "Eliminating instance dictionaries with `__slots__` reduces memory usage by up to 70% and speeds up attribute lookups. This optimization is crucial when handling large datasets in memory.",
      hint: "Define `__slots__` in your record representation class and instantiate them inside a standard list comprehension loop.",
      starterCode: `class FastRecord:\n    __slots__ = ('id', 'value')\n    def __init__(self, id, value):\n        self.id = id\n        self.value = value\n\ndef parse_records(data_list):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "[{'id':1,'value':10}]", call: "str(type(parse_records([{'id':1,'value':10}])[0]))", expected: "<class 'FastRecord'>" },
      ],
    },
    {
      id: 49,
      title: "Context Manager Garbage Collection Interceptor",
      slug: "python-context-manager-garbage-collection-interceptor",
      seoTitle: "Python Safe GC Suppression Context | Memory management | Repractiq",
      metaDescription: "Temporarily pause cyclic garbage collection routines inside critical, high-performance blocks using context managers.",
      tags: ["memory management", "context managers"],
      difficulty: "Hard",
      type: "function",
      description: "Create a context manager called `GCSwitch` that disables automatic garbage collection upon entry to optimize execution speeds, and guarantees that the garbage collector is re-enabled when the context exits.",
      explanation: "Garbage collection pauses can slow down time-sensitive execution blocks. Temporarily disabling the collector ensures maximum throughput, while the context manager guarantees it gets turned back on safely.",
      hint: "Use `gc.isenabled()` to save the initial state, turn it off with `gc.disable()`, and restore the state using `gc.enable()` inside `__exit__`.",
      starterCode: `import gc\n\nclass GCSwitch:\n    def __enter__(self):\n        # your code here\n        pass\n\n    def __exit__(self, exc_type, exc_val, exc_tb):\n        # your code here\n        pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "", call: "with GCSwitch(): pass\nstr(gc.isenabled())", expected: "True" },
      ],
    },
    {
      id: 50,
      title: "Recursive Nested JSON Tree Pruner",
      slug: "python-recursive-nested-json-tree-pruner",
      seoTitle: "Python Recursive JSON Pruning | Advanced recursion | Repractiq",
      metaDescription: "Prune null values from deeply nested dictionaries and array tracking trees recursively.",
      tags: ["advanced recursion", "nested data structures"],
      difficulty: "Hard",
      type: "function",
      description: "Write a recursive function called `prune_empty_nodes` that strips away all keys mapping to empty dictionaries `{}` or `None` values from an arbitrarily nested JSON data structure.",
      explanation: "Cleaning up messy nested tree objects requires exploring every branch recursively. By checking types and removing empty nodes from the bottom up, you can safely sanitize your structural data.",
      hint: "Iterate through keys recursively if the value is a dictionary. Clean up nested layers first before checking if the parent container itself has become empty.",
      starterCode: `def prune_empty_nodes(d):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "{'a': {}, 'b': 1}", call: "str(prune_empty_nodes({'a': {}, 'b': 1}))", expected: "{'b': 1}" },
      ],
    },
    {
      id: 51,
      title: "Decorator with Class Method Introspection",
      slug: "python-decorator-class-method-introspection",
      seoTitle: "Python Decorator Wraps Signature Introspection | Decorators | Repractiq",
      metaDescription: "Build signatures inspection wrappers enforcing precise parameter validation checks using descriptors.",
      tags: ["decorators", "advanced OOP"],
      difficulty: "Hard",
      type: "function",
      description: "Write a decorator called `enforce_types` that reads type annotations on a function. When called, it must validate that the provided arguments match the annotated types, raising a `TypeError` if there is a mismatch.",
      explanation: "Advanced decorators use the `inspect` module to map arguments to their formal parameters. This allows you to build generic validation tools that check data types before executing the underlying function.",
      hint: "Use `inspect.signature(func).bind(*args, **kwargs)` to accurately pair incoming arguments with their annotated types.",
      starterCode: `import functools\nimport inspect\n\ndef enforce_types(func):\n    # your code here\n    return func\n`,
      expectedOutput: "",
      testCases: [
        { input: "", call: "@enforce_types\ndef calc(x: int): return x\ncalc(10)", expected: "10" },
      ],
    },
    {
      id: 52,
      title: "Dynamic Coroutine Pipeline Stream Multiplexer",
      slug: "python-dynamic-coroutine-pipeline-stream-multiplexer",
      seoTitle: "Python Generator Multiplexing Broadcaster Pipeline | Generators | Repractiq",
      metaDescription: "Broadcast incoming data to multiple consumer coroutines concurrently using generator pipelines.",
      tags: ["generators"],
      difficulty: "Hard",
      type: "function",
      description: "Write a generator controller function called `broadcast_stream` that accepts a list of target consumer generators. When data is sent to `broadcast_stream` via `.send()`, it should broadcast that value out to every target consumer.",
      explanation: "Chaining generator targets together creates a clean reactive data pipeline. A central broadcasting generator passes incoming events down to nested sub-consumers without needing complex thread management.",
      hint: "Loop through the list of sub-generators and forward the input using `target.send(value)` inside a `while True:` loop.",
      starterCode: `def broadcast_stream(targets):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "", call: "hasattr(broadcast_stream([]), 'send')", expected: "True" },
      ],
    },
    {
      id: 53,
      title: "Safe System Resource Allocation Context",
      slug: "python-safe-system-resource-allocation-context",
      seoTitle: "Python Contextlib Closing Primitives | Context Managers | Repractiq",
      metaDescription: "Manage life cycle allocations safely for standard socket and hardware streams using cleanup managers.",
      tags: ["context managers"],
      difficulty: "Hard",
      type: "function",
      description: "Create a class-based context manager named `SafeResource` that allocates a mock network descriptor socket channel. It must guarantee that `close()` is called on the resource, even if the runtime block throws an error.",
      explanation: "Leaking system resources like sockets or file handles can crash long-running applications. Context managers prevent leaks by guaranteeing that cleanup code in `__exit__` always runs.",
      hint: "Ensure that `self.resource.close()` is executed inside the `__exit__` block collection.",
      starterCode: `class SafeResource:\n    def __init__(self, resource):\n        self.res = resource\n\n    def __enter__(self):\n        return self.res\n\n    def __exit__(self, exc_type, exc_val, exc_tb):\n        # your code here\n        pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "", call: "class MockRes: \n def close(self): self.c=1\nm = MockRes(); \nwith SafeResource(m): pass\nm.c", expected: "1" },
      ],
    },
    {
      id: 54,
      title: "API Routing Metaclass Compiler",
      slug: "python-api-routing-metaclass-compiler",
      seoTitle: "Python Class Route Decoration Metaclass | Metaclasses concepts | Repractiq",
      metaDescription: "Register application controller paths automatically into a central router layout using metaclasses.",
      tags: ["metaclasses concepts", "design patterns"],
      difficulty: "Hard",
      type: "function",
      description: "Write a metaclass called `RouterMeta` that scans class functions for a custom `_route` string attribute, mapping these routes into a global dictionary named `URL_ROUTER`.",
      explanation: "Web frameworks often use metaclasses to discover routes automatically. Scanning methods during class definition allows you to build central routing tables without requiring manual registration.",
      hint: "Inspect the class attributes directory inside `__new__` or `__init__`, looking for custom markers left by decorators.",
      starterCode: `URL_ROUTER = {}\n\nclass RouterMeta(type):\n    def __init__(cls, name, bases, dct):\n        # your code here\n        super().__init__(name, bases, dct)\n`,
      expectedOutput: "",
      testCases: [
        { input: "", call: "isinstance(RouterMeta, type)", expected: "True" },
      ],
    },
    {
      id: 55,
      title: "Async Dynamic Polling Task Manager",
      slug: "python-async-dynamic-polling-task-manager",
      seoTitle: "Python Asyncio Cancellation Event Management | Async basics | Repractiq",
      metaDescription: "Manage background pooling loops safely using cooperative task cancellation tokens in asyncio.",
      tags: ["async basics"],
      difficulty: "Hard",
      type: "function",
      description: "Write an asynchronous orchestrator called `cancel_slow_polling` that starts a long-running background task, waits for a specified timeout, and safely cancels the task using `.cancel()` if it hasn't finished, handling the cancellation gracefully.",
      explanation: "Unbounded background loops can hang applications if they get stuck. Wrapping tasks with timeouts or calling `task.cancel()` allows you to recover resources safely when a task runs long.",
      hint: "Catch `asyncio.CancelledError` inside your background task coroutine to handle cleanup when it gets cancelled.",
      starterCode: `import asyncio\n\nasync def cancel_slow_polling(coro_target, timeout_limit):\n    // your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "", call: "import asyncio; asyncio.iscoroutinefunction(cancel_slow_polling)", expected: "True" },
      ],
    },
    {
      id: 56,
      title: "Dynamic Abstract Property Implementation",
      slug: "python-dynamic-abstract-property-implementation",
      seoTitle: "Python ABC Abstractproperty Overriding | Advanced OOP | Repractiq",
      metaDescription: "Enforce abstract property requirements across base class inheritance models.",
      tags: ["advanced OOP"],
      difficulty: "Hard",
      type: "function",
      description: "Design a configuration component hierarchy using `abc.ABC`. Create a base class `DatabaseConnector` that defines an abstract property `connection_string`. Any concrete subclass must implement this property or fail initialization.",
      explanation: "Abstract properties act as interface contracts for attributes. Using `@property` combined with `@abc.abstractmethod` ensures that subclasses define required attributes, preventing configuration errors at runtime.",
      hint: "Decorate the base class method with both `@property` and `@abc.abstractmethod`.",
      starterCode: `import abc\n\nclass DatabaseConnector(abc.ABC):\n    @property\n    @abc.abstractmethod\n    def connection_string(self):\n        pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "", call: "hasattr(DatabaseConnector, 'connection_string')", expected: "True" },
      ],
    },
    {
      id: 57,
      title: "Facade Pattern System Micro-Orchestrator",
      slug: "python-facade-pattern-system-micro-orchestrator",
      seoTitle: "Python Facade Complex Subsystem Simplification | Design Patterns | Repractiq",
      metaDescription: "Simplify access to complex subsystem logic behind a unified, easy-to-use Facade interface.",
      tags: ["design patterns", "advanced OOP"],
      difficulty: "Hard",
      type: "function",
      description: "Design a processing pipeline using the Facade pattern. Combine complex separate tasks (`Encoder`, `Uploader`, `Notifier`) into a single, straightforward wrapper class called `VideoProcessorFacade` that handles the entire workflow via one method call.",
      explanation: "The Facade pattern hides system complexity behind a simple interface. This keeps client code clean and decoupled from the internal details of multi-step subsystems.",
      hint: "Instantiate the subsystem classes inside the facade's initializer, and coordinate their steps within a unified execution method.",
      starterCode: `class VideoProcessorFacade:\n    def __init__(self):\n        # your code here\n        pass\n\n    def process_video(self, video_path):\n        # your code here\n        pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "", call: "hasattr(VideoProcessorFacade, 'process_video')", expected: "True" },
      ],
    },
    {
      id: 58,
      title: "Generator Execution Pipeline Performance Optimizer",
      slug: "python-generator-execution-pipeline-performance-optimizer",
      seoTitle: "Python Generator Chaining Stream Speed | Performance optimization | Repractiq",
      metaDescription: "Optimize multi-stage data pipelines by chaining generators together to avoid creating large intermediate lists.",
      tags: ["performance optimization", "generators"],
      difficulty: "Hard",
      type: "function",
      description: "Write a high-speed data filtering pipeline called `fast_pipeline` that chains multiple generator expressions together to transform and filter a large collection of records without creating any intermediate lists in memory.",
      explanation: "Chaining generators keeps memory usage low by processing records one at a time. This avoids the overhead of allocating large temporary arrays, which speeds up data filtering tasks.",
      hint: "Pass the output of one generator expression directly as the input to the next stage in the pipeline.",
      starterCode: `def fast_pipeline(raw_stream):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "[1, 2, 3]", call: "str(type(fast_pipeline([1,2,3])))", expected: "<class 'generator'>" },
      ],
    },
    {
      id: 59,
      title: "Weak Value Dictionary Cache Store",
      slug: "python-weak-value-dictionary-cache-store",
      seoTitle: "Python WeakValueDictionary Reference Caching | Memory management | Repractiq",
      metaDescription: "Build automatic cache stores that allow the garbage collector to free up values when they are no longer in use.",
      tags: ["memory management"],
      difficulty: "Hard",
      type: "function",
      description: "Create an efficient instance cache manager called `InstanceCache` using `weakref.WeakValueDictionary`. It should cache objects automatically while allowing them to be garbage collected when no other parts of the application hold a reference.",
      explanation: "Standard dictionaries keep objects alive by holding a strong reference to them, which can cause memory leaks. A `WeakValueDictionary` drops these references automatically when the object is no longer used elsewhere, cleaning up memory without manual intervention.",
      hint: "Use `weakref.WeakValueDictionary()` to store your cached values.",
      starterCode: `import weakref\n\nclass InstanceCache:\n    def __init__(self):\n        self.cache = weakref.WeakValueDictionary()\n\n    def get_cached(self, key):\n        # your code here\n        pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "", call: "isinstance(InstanceCache().cache, weakref.WeakValueDictionary)", expected: "True" },
      ],
    },
    {
      id: 60,
      title: "Recursive Directory Tree Node Counter",
      slug: "python-recursive-directory-tree-node-counter",
      seoTitle: "Python Recursive Directory File Walking | Advanced recursion | Repractiq",
      metaDescription: "Count the total number of files in nested directory structures using recursive filesystem algorithms.",
      tags: ["advanced recursion", "file handling concepts"],
      difficulty: "Hard",
      type: "function",
      description: "Write a recursive function called `count_all_files` that navigates a mock directory tree object (nested dictionaries representing folders and lists representing files) and returns the total file count.",
      explanation: "Deep file structures are best traversed using recursion. A recursive function counts files at the current level and branches into subdirectories automatically, handling nested files of any depth.",
      hint: "Loop through items in the dictionary. If an item is a dictionary itself, recursively call `count_all_files` on it and add the result to your total.",
      starterCode: `def count_all_files(mock_dir):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "{'a': [1, 2], 'b': {}}", call: "count_all_files({'a': [1, 2], 'b': {}})", expected: "2" },
      ],
    },
    {
      id: 61,
      title: "Parameterized Hook Execution Decorator",
      slug: "python-parameterized-hook-execution-decorator",
      seoTitle: "Python Parameterized Decorator Factory Hooks | Decorators | Repractiq",
      metaDescription: "Design custom decorator factories that register callback hooks dynamically based on operational event topics.",
      tags: ["decorators"],
      difficulty: "Hard",
      type: "function",
      description: "Write a decorator factory called `on_event(event_name)` that registers the decorated function into a global dictionary named `EVENT_HOOKS`. Multiple functions should be able to register under the same event name as a list of callbacks.",
      explanation: "Decorator factories take arguments and return the actual decorator function. This is perfect for event systems, allowing you to attach callback functions to specific event topics at definition time.",
      hint: "Check if the event name already exists in `EVENT_HOOKS`. If it doesn't, initialize an empty list before appending the function.",
      starterCode: `EVENT_HOOKS = {}\n\ndef on_event(event_name):\n    def decorator(func):\n        # your code here\n        return func\n    return decorator\n`,
      expectedOutput: "",
      testCases: [
        { input: "", call: "@on_event('start')\ndef init(): pass\n'start' in EVENT_HOOKS", expected: "True" },
      ],
    },
    {
      id: 62,
      title: "Stateful Coroutine Stream Processor",
      slug: "python-stateful-coroutine-stream-processor",
      seoTitle: "Python Generator Send Running Sum | Generators | Repractiq",
      metaDescription: "Process streams of data efficiently using stateful generator coroutines that accept updates via the send protocol.",
      tags: ["generators"],
      difficulty: "Hard",
      type: "function",
      description: "Write a generator function called `running_accumulator` that acts as a consumer coroutine. It should accept numbers via `.send(value)` and yield the updated cumulative total after each new value is received.",
      explanation: "Using `yield` as an expression allows a generator to receive data when `.send()` is called. This makes it easy to build memory-efficient state tracking tools, like calculators that process data streams on the fly.",
      hint: "Start with an internal total variable set to 0, run an infinite loop, and update the total with `val = yield total`.",
      starterCode: `def running_accumulator():\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "", call: "g = running_accumulator(); next(g); g.send(5); g.send(10)", expected: "15" },
      ],
    },
    {
      id: 63,
      title: "Thread-Safe Context Lock Manager",
      slug: "python-thread-safe-context-lock-manager",
      seoTitle: "Python Threading Lock Context Wrapper | Context Managers | Repractiq",
      metaDescription: "Prevent race conditions in multi-threaded programs by managing synchronization locks using context managers.",
      tags: ["context managers"],
      difficulty: "Hard",
      type: "function",
      description: "Create a class-based context manager named `SafeLockContext` that accepts a `threading.Lock` object. It should acquire the lock upon entry and ensure the lock is safely released when exiting, even if the internal block crashes.",
      explanation: "Forgetting to release a lock can cause deadlocks that freeze your application. Wrapping your lock operations inside a context manager guarantees that the lock is always released safely when the block finishes.",
      hint: "Call `self.lock.acquire()` inside `__enter__` and `self.lock.release()` inside `__exit__`.",
      starterCode: `import threading\n\nclass SafeLockContext:\n    def __init__(self, lock_obj):\n        self.lock = lock_obj\n\n    def __enter__(self):\n        # your code here\n        return self\n\n    def __exit__(self, exc_type, exc_val, exc_tb):\n        # your code here\n        pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "", call: "import threading; hasattr(SafeLockContext(threading.Lock()), '__exit__')", expected: "True" },
      ],
    },
    {
      id: 64,
      title: "Immutable Attribute Enforcement Metaclass",
      slug: "python-immutable-attribute-enforcement-metaclass",
      seoTitle: "Python Metaclass Immutable Attributes Setattr | Metaclasses concepts | Repractiq",
      metaDescription: "Create strictly immutable data structures by intercepting attribute modifications using custom metaclasses.",
      tags: ["metaclasses concepts", "advanced OOP"],
      difficulty: "Hard",
      type: "function",
      description: "Write a metaclass called `ImmutableMeta` that modifies class definitions so that any attributes set during initialization cannot be altered or deleted later, raising an `AttributeError` on any modification attempt.",
      explanation: "Metaclasses let you change class behavior at a deep level. Overriding the `__setattr__` and `__delattr__` methods on the generated class allows you to block changes and enforce immutability.",
      hint: "Inside the class creation process, override `__setattr__` to throw an error if an attribute is modified after the object has been initialized.",
      starterCode: `class ImmutableMeta(type):\n    def __new__(cls, name, bases, dct):\n        # your code here\n        return super().__new__(cls, name, bases, dct)\n`,
      expectedOutput: "",
      testCases: [
        { input: "", call: "isinstance(ImmutableMeta, type)", expected: "True" },
      ],
    },
    {
      id: 65,
      title: "Async Microservice Request Throttler",
      slug: "python-async-microservice-request-throttler",
      seoTitle: "Python Asyncio Sleep Rate Limiter | Async basics | Repractiq",
      metaDescription: "Protect remote APIs from high-traffic overload by building a rate-limiting request throttler using asyncio.",
      tags: ["async basics"],
      difficulty: "Hard",
      type: "function",
      description: "Write an asynchronous coroutine called `throttled_request_batch` that processes a list of tasks. It must introduce a fixed async delay using `asyncio.sleep` between tasks to ensure the execution rate stays within a safe API limit.",
      explanation: "Sending too many requests at once can overwhelm external APIs and trigger rate limits. Adding controlled delays with `asyncio.sleep` allows you to throttle throughput and keep traffic at a safe level.",
      hint: "Loop through your tasks sequentially, await each task's execution, and pause the loop with `await asyncio.sleep(delay)` between rounds.",
      starterCode: `import asyncio\n\nasync def throttled_request_batch(tasks, delay_seconds):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "", call: "import asyncio; asyncio.iscoroutinefunction(throttled_request_batch)", expected: "True" },
      ],
    },
    {
      id: 66,
      title: "Advanced OOP Mixin Method Resolution",
      slug: "python-advanced-oop-mixin-method-resolution",
      seoTitle: "Python Mixin Class Composition | Advanced OOP | Repractiq",
      metaDescription: "Extend class behaviors cleanly without breaking inheritance chains by using custom modular mixins.",
      tags: ["advanced OOP"],
      difficulty: "Hard",
      type: "function",
      description: "Create a modular logging mixin class called `JSONSerializableMixin` that implements a `to_json()` method. This method should automatically serialize the object's instance attributes into a JSON string using `json.dumps`.",
      explanation: "Mixins allow you to add reusable functionality to classes without using complex multiple inheritance. They keep code modular by focusing on a single capability that can be plugged into any class.",
      hint: "Use `json.dumps(self.__dict__)` inside the mixin's method to automatically export all instance variables.",
      starterCode: `import json\n\nclass JSONSerializableMixin:\n    def to_json(self):\n        # your code here\n        pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "", call: "class User(JSONSerializableMixin):\n def __init__(self): self.id=1\nUser().to_json()", expected: '{"id": 1}' },
      ],
    },
    {
      id: 67,
      title: "Structural Adapter Pattern Protocol Converter",
      slug: "python-structural-adapter-pattern-protocol-converter",
      seoTitle: "Python Adapter Pattern Target Translation | Design Patterns | Repractiq",
      metaDescription: "Connect mismatched class interfaces smoothly by building a structural Adapter pattern bridge.",
      tags: ["design patterns", "advanced OOP"],
      difficulty: "Hard",
      type: "function",
      description: "Design an integration bridge using the Adapter pattern. Create an `XMLRequestAdapter` class that translates legacy XML data formats into a modern structural JSON object required by a third-party analytics service.",
      explanation: "The Adapter pattern lets classes with incompatible interfaces work together. It wraps the legacy class in an adapting structure, translating method calls between old formats and new requirements.",
      hint: "Store a reference to the legacy component inside your adapter class, and handle data format conversion inside the main execution method.",
      starterCode: `class LegacyXMLService:\n    def get_xml_data(self): return "<data><id>1</id></data>"\n\nclass XMLRequestAdapter:\n    def __init__(self, service):\n        self.service = service\n\n    def get_json(self):\n        # your code here\n        pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "", call: "hasattr(XMLRequestAdapter(LegacyXMLService()), 'get_json')", expected: "True" },
      ],
    },
    {
      id: 68,
      title: "High-Performance Local Cache Storage Optimizer",
      slug: "python-high-performance-local-cache-storage-optimizer",
      seoTitle: "Python Dict Hashing Benchmark Performance | Performance optimization | Repractiq",
      metaDescription: "Speed up high-frequency dictionary lookups by optimizing key hashing and string construction strategies.",
      tags: ["performance optimization"],
      difficulty: "Hard",
      type: "function",
      description: "Write a fast caching dictionary class called `FastLookupStore`. Implement optimized methods for storing and retrieving records, using pre-calculated hash indices to maximize performance during heavy lookups.",
      explanation: "Dictionary lookups are generally quick, but generating complex string keys on the fly can slow things down. Pre-calculating integer hash keys minimizes overhead, keeping lookups fast even under heavy loads.",
      hint: "Use the built-in `hash()` function to turn complex search keys into simple integer lookups inside your internal storage map.",
      starterCode: `class FastLookupStore:\n    def __init__(self):\n        self.store = {}\n\n    def get_fast(self, key):\n        # your code here\n        pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "", call: "hasattr(FastLookupStore, 'get_fast')", expected: "True" },
      ],
    },
    {
      id: 69,
      title: "Manual Reference Counting Diagnostic Pointer",
      slug: "python-manual-reference-counting-diagnostic-pointer",
      seoTitle: "Python Reference Inspection Sys Getrefcount | Memory management | Repractiq",
      metaDescription: "Monitor object reference lifecycles closely using low-level system tracking diagnostics.",
      tags: ["memory management"],
      difficulty: "Hard",
      type: "function",
      description: "Write a diagnostics function called `track_reference_count` that creates an object, assigns multiple references to it, and prints out the internal reference count changes using `sys.getrefcount` at each step.",
      explanation: "Python clears objects from memory as soon as their reference count drops to zero. Tracking these count shifts with `sys.getrefcount()` helps you spot accidental references that could cause memory leaks.",
      hint: "Remember that passing an object to `sys.getrefcount()` temporarily adds one to the count during the check.",
      starterCode: `import sys\n\ndef track_reference_count():\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "", call: "str(type(track_reference_count()))", expected: "<class 'NoneType'>" },
      ],
    },
    {
      id: 70,
      title: "Recursive Nested Structural Schema Validator",
      slug: "python-recursive-nested-structural-schema-validator",
      seoTitle: "Python Recursive Dict Schema Validation | Advanced recursion | Repractiq",
      metaDescription: "Validate complex, deeply nested JSON dictionary structures against a predefined schema dictionary using recursion.",
      tags: ["advanced recursion", "nested data structures"],
      difficulty: "Hard",
      type: "function",
      description: "Write a recursive validation function called `validate_schema(data, schema)` that compares a nested dictionary against a structural template dictionary. It should return `True` if all key types match perfectly, and `False` otherwise.",
      explanation: "Checking complex, nested datasets requires an elastic validation strategy. A recursive function walks down matching paths of both the data and the template, ensuring all types align perfectly at every level.",
      hint: "Loop through the schema template keys. If a value is a dictionary, use a recursive call; otherwise, verify the data type using `isinstance()` directly.",
      starterCode: `def validate_schema(data, schema):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "{'a':1}, {'a':int}", call: "validate_schema({'a':1}, {'a':int})", expected: "True" },
      ],
    },
    {
      id: 71,
      title: "Memoization Decorator with Expiry Time",
      slug: "python-memoization-decorator-expiry-time",
      seoTitle: "Python TTL Memoize Decorator | Decorators | Repractiq",
      metaDescription: "Learn to build an advanced caching decorator that automatically invalidates stale entries after a time-to-live (TTL) threshold.",
      tags: ["decorators", "performance optimization"],
      difficulty: "Hard",
      type: "function",
      description: "Write a parameterized decorator called `memoize_with_ttl(ttl_seconds)` that caches the result of a function call. If the function is called again with the same arguments after `ttl_seconds` have passed since the cache entry was created, the function must be re-run and the cache updated.",
      explanation: "Caching values reduces execution overhead, but stale data can cause application errors. Integrating a Time-To-Live (TTL) timestamp check into your decorator's closure allows for automatic cache expiration.",
      hint: "Store a tuple of `(result, creation_timestamp)` mapped to your hashed arguments key, and check `time.time() - creation_timestamp > ttl_seconds`.",
      starterCode: `import time\nimport functools\n\ndef memoize_with_ttl(ttl_seconds):\n    def decorator(func):\n        cache = {}\n        # your code here\n        return func\n    return decorator\n`,
      expectedOutput: "",
      testCases: [
        { input: "", call: "hasattr(memoize_with_ttl(5), '__call__')", expected: "True" },
      ],
    },
    {
      id: 72,
      title: "Generator-based Pipeline with Error Recovery",
      slug: "python-generator-pipeline-error-recovery",
      seoTitle: "Python Resilient Generator Pipeline | Generators | Repractiq",
      metaDescription: "Construct a multi-stage generator processing pipeline capable of catching and recovering from inline exceptions.",
      tags: ["generators"],
      difficulty: "Hard",
      type: "function",
      description: "Write a generator pipeline function named `resilient_pipeline` that accepts a sequence of numbers. It must yield the square of each number, but if any element is non-numeric, it should catch the exception, yield a fallback string value `'INVALID'`, and continue processing subsequent items.",
      explanation: "Standard generator loops terminate immediately when an unhandled exception is raised. Building granular try-except blocks inside the loop body guarantees processing continuity across raw streaming data payloads.",
      hint: "Use a clean `try...except TypeError` block inside your generator's iteration loop to keep the generator alive.",
      starterCode: `def resilient_pipeline(data_stream):\n    for item in data_stream:\n        # your code here\n        pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "[1, 'bad', 3]", call: "list(resilient_pipeline([1, 'bad', 3]))", expected: "[1, 'INVALID', 9]" },
      ],
    },
    {
      id: 73,
      title: "Environment Variable Override Context Manager",
      slug: "python-environment-variable-override-context-manager",
      seoTitle: "Python Mock OS Environ Context Manager | Context Managers | Repractiq",
      metaDescription: "Safely isolate and override system environment variables within scoped execution code blocks.",
      tags: ["context managers"],
      difficulty: "Hard",
      type: "function",
      description: "Write a class-based context manager named `MockEnvironment` that accepts a dictionary of temporary environment variables. It should update `os.environ` upon entering, and restore the environment variables to their exact original state when exiting.",
      explanation: "Testing modules that read system environment flags requires surgical scoping. Storing the previous configuration state before modification allows context managers to undo temporary system changes during exiting phases.",
      hint: "Capture copies of matching keys from `os.environ` inside your `__enter__` block, and restore or delete them inside `__exit__`.",
      starterCode: `import os\n\nclass MockEnvironment:\n    def __init__(self, temp_env_dict):\n        self.temp_env = temp_env_dict\n        self.original_env = {}\n\n    def __enter__(self):\n        # your code here\n        pass\n\n    def __exit__(self, exc_type, exc_val, exc_tb):\n        # your code here\n        pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "", call: "hasattr(MockEnvironment({}), '__exit__')", expected: "True" },
      ],
    },
    {
      id: 74,
      title: "Singleton Class Metaclass Guard",
      slug: "python-singleton-class-metaclass-guard",
      seoTitle: "Python Metaclass Singleton Pattern | Metaclasses concepts | Repractiq",
      metaDescription: "Enforce a strict Singleton pattern across your classes using custom metaclass interceptors.",
      tags: ["metaclasses concepts", "design patterns"],
      difficulty: "Hard",
      type: "function",
      description: "Write a metaclass called `SingletonMeta` that prevents more than one instance of a class from being created. Subsequent instantiation attempts must return the identical initial instance reference.",
      explanation: "Metaclasses intercept the execution of a class's `__call__` method. By storing active instance references within a private dictionary, you can enforce centralized resource managers dynamically.",
      hint: "Override the `__call__` method in `SingletonMeta` to check if an instance of the class is already stored in an internal `_instances` dictionary.",
      starterCode: `class SingletonMeta(type):\n    _instances = {}\n    def __call__(cls, *args, **kwargs):\n        # your code here\n        pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "", call: "class S(metaclass=SingletonMeta): pass\nS() is S()", expected: "True" },
      ],
    },
    {
      id: 75,
      title: "Asyncio Queue Producer-Consumer Hub",
      slug: "python-asyncio-queue-producer-consumer-hub",
      seoTitle: "Python Async Queue Pipeline | Async basics | Repractiq",
      metaDescription: "Coordinate and execute safe, concurrent pipeline exchanges using asyncio's internal FIFO queues.",
      tags: ["async basics"],
      difficulty: "Hard",
      type: "function",
      description: "Write an asynchronous orchestrator called `run_async_pipeline` that sets up an `asyncio.Queue`. Create a producer coroutine that populates the queue with messages and a consumer coroutine that processes them until the queue is empty.",
      explanation: "Queues decouple production processes from consumption routines. In asyncio, using waitable queues prevents blocking while sharing payloads across running tasks.",
      hint: "Use `await queue.put()` to enqueue items and `await queue.get()` followed by `queue.task_done()` inside consumers.",
      starterCode: `import asyncio\n\nasync def run_async_pipeline(items):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "", call: "import asyncio; asyncio.iscoroutinefunction(run_async_pipeline)", expected: "True" },
      ],
    },
    {
      id: 76,
      title: "Custom Dictionary Descriptor with Validation",
      slug: "python-custom-dictionary-descriptor-with-validation",
      seoTitle: "Python Descriptor Class Schema Validation | Advanced OOP | Repractiq",
      metaDescription: "Implement strict, structural runtime schema validation using custom descriptors on instance dictionaries.",
      tags: ["advanced OOP"],
      difficulty: "Hard",
      type: "function",
      description: "Write a descriptor class called `ValidatedDictDescriptor` that intercepts writes to an attribute. The attribute must always be a dictionary, and the descriptor must validate that all values within the dictionary are positive integers, raising a `ValueError` otherwise.",
      explanation: "Descriptors manage variable lookups, modifications, and deletions. Overriding `__set__` lets you validate incoming nested structures before they are saved to the instance dictionary.",
      hint: "Implement standard descriptor methods `__get__` and `__set__`, checking the dictionary types and values inside `__set__`.",
      starterCode: `class ValidatedDictDescriptor:\n    def __init__(self, name):\n        self.name = name\n\n    def __get__(self, instance, owner):\n        # your code here\n        pass\n\n    def __set__(self, instance, value):\n        # your code here\n        pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "", call: "hasattr(ValidatedDictDescriptor('data'), '__set__')", expected: "True" },
      ],
    },
    {
      id: 77,
      title: "Observer Pattern Event Bus Router",
      slug: "python-observer-pattern-event-bus-router",
      seoTitle: "Python Observer Subject Pattern | Design Patterns | Repractiq",
      metaDescription: "Build a decoupled, event-driven observer communication layer allowing dynamically registered modules to communicate.",
      tags: ["design patterns", "advanced OOP"],
      difficulty: "Hard",
      type: "function",
      description: "Design an `EventBus` class using the Observer pattern. It must support registering listener functions to specific event keys via `subscribe(event_type, callback)` and executing all registered callbacks using `publish(event_type, data)`.",
      explanation: "The Observer pattern decouples event publishers from their consumers. A central class acts as an event dispatcher, allowing systems to communicate without direct dependencies.",
      hint: "Map event names to lists of subscriber callbacks inside a dictionary. Loop through and execute these callbacks during publication.",
      starterCode: `class EventBus:\n    def __init__(self):\n        self.subscribers = {}\n\n    def subscribe(self, event_type, callback):\n        # your code here\n        pass\n\n    def publish(self, event_type, data):\n        # your code here\n        pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "", call: "bus = EventBus(); log = []; bus.subscribe('e', log.append); bus.publish('e', 1); log", expected: "[1]" },
      ],
    },
    {
      id: 78,
      title: "High-Performance Memory Buffer Optimizer",
      slug: "python-high-performance-memory-buffer-optimizer",
      seoTitle: "Python Bytearray Memoryview Stream Performance | Performance optimization | Repractiq",
      metaDescription: "Accelerate raw stream slicing operations by using zero-copy memoryviews on large underlying arrays.",
      tags: ["performance optimization", "memory management"],
      difficulty: "Hard",
      type: "function",
      description: "Write a utility called `process_buffer_zero_copy` that processes a large byte array in place using `memoryview`. It must zero out specific parts of the byte array without creating any temporary list copies in memory.",
      explanation: "Slicing large lists or strings creates copies in memory, which can slow down performance. Using `memoryview` allows you to access and modify memory blocks directly, preventing unnecessary allocations.",
      hint: "Create a `memoryview` of the bytearray, and modify slices of that view directly.",
      starterCode: `def process_buffer_zero_copy(byte_arr, start_idx, end_idx):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "bytearray([1, 2, 3])", call: "b = bytearray([1, 2, 3]); process_buffer_zero_copy(b, 0, 2); list(b)", expected: "[0, 0, 3]" },
      ],
    },
    {
      id: 79,
      title: "Low-Memory Generator Chunk Reader",
      slug: "python-low-memory-generator-chunk-reader",
      seoTitle: "Python Generator Chunked File Stream | Memory management | Repractiq",
      metaDescription: "Stream and process massive datasets efficiently using generators to read records in chunks rather than loading files all at once.",
      tags: ["memory management", "generators"],
      difficulty: "Hard",
      type: "function",
      description: "Write a generator function called `read_in_chunks` that takes an iterable and a chunk size. It must yield slices of the data as lists of the specified size, ensuring that the full dataset is never stored in memory at once.",
      explanation: "Loading entire large datasets into memory can quickly cause out-of-memory crashes. Generators resolve this issue by yielding records in small, manageable chunks on demand.",
      hint: "Use an iterator and `itertools.islice()` to yield small chunks from the data stream in a loop.",
      starterCode: `from itertools import islice\n\ndef read_in_chunks(iterable, chunk_size):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "[1,2,3,4,5], 2", call: "list(read_in_chunks([1,2,3,4,5], 2))", expected: "[[1, 2], [3, 4], [5]]" },
      ],
    },
    {
      id: 80,
      title: "Recursive Nested Sequence Flattener",
      slug: "python-recursive-nested-sequence-flattener",
      seoTitle: "Python Recursively Flatten Lists Tuples | Advanced recursion | Repractiq",
      metaDescription: "Flatten deeply nested collections of lists and tuples into a single continuous sequence using recursion.",
      tags: ["advanced recursion", "nested data structures"],
      difficulty: "Hard",
      type: "function",
      description: "Write a recursive generator function called `recursive_flatten` that accepts an arbitrarily nested sequence of iterables (lists, tuples) and yields a flat sequence of individual, non-iterable elements.",
      explanation: "Deep, nested structural sequences are difficult to process with simple loops. A recursive generator traverses through nested sequences, drilling down into nested lists and yielding items as they are found.",
      hint: "Check if the item is iterable (but not a string). If it is, recursively yield from it; otherwise, yield the item directly.",
      starterCode: `def recursive_flatten(nested_sequence):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "[1, [2, (3, 4)]]", call: "list(recursive_flatten([1, [2, (3, 4)]]))", expected: "[1, 2, 3, 4]" },
      ],
    },
    {
      id: 81,
      title: "Sliding Window Rate Limiter Decorator",
      slug: "python-sliding-window-rate-limiter-decorator",
      seoTitle: "Python Decorator Rate Limiting Calls | Decorators | Repractiq",
      metaDescription: "Protect critical endpoints from abuse using rate-limiting decorators that track rolling time windows.",
      tags: ["decorators"],
      difficulty: "Hard",
      type: "function",
      description: "Write a parameterized decorator called `rate_limit(max_calls, period_seconds)` that restricts function execution. If the function is called more than `max_calls` within a rolling window of `period_seconds`, raise a `RuntimeError`.",
      explanation: "Decorators are excellent for implementing security controls. By tracking timestamps of recent calls within a closure-based list, you can restrict access dynamically when call volume exceeds set thresholds.",
      hint: "Maintain a list of timestamps inside your wrapper. Remove timestamps older than `current_time - period_seconds` before validating the limit.",
      starterCode: `import time\nimport functools\n\ndef rate_limit(max_calls, period_seconds):\n    def decorator(func):\n        calls = []\n        # your code here\n        return func\n    return decorator\n`,
      expectedOutput: "",
      testCases: [
        { input: "", call: "hasattr(rate_limit(1, 1), '__call__')", expected: "True" },
      ],
    },
    {
      id: 82,
      title: "Bidirectional Coroutine Processing Channel",
      slug: "python-bidirectional-coroutine-processing-channel",
      seoTitle: "Python Coroutine Send Yield Interconnect | Generators | Repractiq",
      metaDescription: "Connect coroutines into a self-contained processing pipeline using bidirectional generators and the `.send()` protocol.",
      tags: ["generators"],
      difficulty: "Hard",
      type: "function",
      description: "Create a coroutine pair named `data_transformer`. The first coroutine modifies raw data values and passes them to the second coroutine, which stores and outputs a running list of results.",
      explanation: "Coroutines are active processes that yield control back and forth while exchanging data. This enables you to build processing networks without needing complex multi-threaded structures.",
      hint: "Set up target generator consumers, and use `target.send(processed_value)` to pass data down the line.",
      starterCode: `def consumer_node():\n    # your code here\n    pass\n\ndef producer_node(target):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "", call: "hasattr(consumer_node(), 'send')", expected: "True" },
      ],
    },
    {
      id: 83,
      title: "Safe Database Transaction Scope Simulation",
      slug: "python-safe-database-transaction-scope-simulation",
      seoTitle: "Python Database Context Manager Rollback | Context Managers | Repractiq",
      metaDescription: "Secure application state transactions by rolling back changes automatically if an exception is raised inside a context block.",
      tags: ["context managers"],
      difficulty: "Hard",
      type: "function",
      description: "Write a context manager called `TransactionScope` that takes a database connection object. It should commit the changes if the block finishes successfully, or roll back all changes if an exception occurs.",
      explanation: "Context managers keep database operations reliable. Overriding the exit block allows you to catch and handle errors, ensuring modifications are committed or safely discarded.",
      hint: "Use a `try...except...else` block inside `__exit__`. If an exception occurs, call `rollback()`; otherwise, run `commit()`.",
      starterCode: `class TransactionScope:\n    def __init__(self, db_conn):\n        self.conn = db_conn\n\n    def __enter__(self):\n        return self.conn\n\n    def __exit__(self, exc_type, exc_val, exc_tb):\n        # your code here\n        pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "", call: "class Conn: \n def commit(self): self.c=1\n def rollback(self): self.r=1\nc = Conn(); \nwith TransactionScope(c): pass\nhasattr(c, 'c')", expected: "True" },
      ],
    },
    {
      id: 84,
      title: "Interface Compliance Metaclass Enforcer",
      slug: "python-interface-compliance-metaclass-enforcer",
      seoTitle: "Python Metaclass Interface Requirements | Metaclasses concepts | Repractiq",
      metaDescription: "Verify interface requirements across your subclasses automatically during module import stages using custom metaclasses.",
      tags: ["metaclasses concepts", "advanced OOP"],
      difficulty: "Hard",
      type: "function",
      description: "Write a metaclass called `InterfaceEnforcerMeta` that reads a class attribute list named `_required_methods`. Any concrete subclass must implement all methods listed there, or the compiler will raise a `TypeError` during class creation.",
      explanation: "While abstract base classes are helpful, metaclasses allow you to customize inheritance validation. They let you run static checks during import, flagging missing methods before code runs.",
      hint: "Inspect the names of defined class methods during metaclass construction inside `__new__`.",
      starterCode: `class InterfaceEnforcerMeta(type):\n    def __new__(cls, name, bases, dct):\n        # your code here\n        return super().__new__(cls, name, bases, dct)\n`,
      expectedOutput: "",
      testCases: [
        { input: "", call: "isinstance(InterfaceEnforcerMeta, type)", expected: "True" },
      ],
    },
    {
      id: 85,
      title: "Async Dynamic Throttling Semaphore Guard",
      slug: "python-async-dynamic-throttling-semaphore-guard",
      seoTitle: "Python Asyncio Semaphore Restrict Tasks | Async basics | Repractiq",
      metaDescription: "Limit and throttle concurrent operations using asyncio Semaphore locks within your task queues.",
      tags: ["async basics"],
      difficulty: "Hard",
      type: "function",
      description: "Write an asynchronous orchestrator called `limit_concurrency` that takes a list of tasks and a concurrency limit. Run all tasks concurrently, but use an `asyncio.Semaphore` to ensure only `limit` tasks are executed at any given moment.",
      explanation: "Running too many tasks concurrently can overwhelm external services and cause application timeouts. Semaphores manage this by letting tasks coordinate access to limited resources.",
      hint: "Acquire the semaphore using `async with semaphore:` before executing each background task.",
      starterCode: `import asyncio\n\nasync def limit_concurrency(tasks, limit_number):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "", call: "import asyncio; asyncio.iscoroutinefunction(limit_concurrency)", expected: "True" },
      ],
    },
    {
      id: 86,
      title: "Dynamic Attribute Proxy Delegate",
      slug: "python-dynamic-attribute-proxy-delegate",
      seoTitle: "Python Class Attribute Getattr Delegation | Advanced OOP | Repractiq",
      metaDescription: "Forward undefined attribute and method lookups dynamically to nested target objects.",
      tags: ["advanced OOP"],
      difficulty: "Hard",
      type: "function",
      description: "Design a class called `AttributeProxy` that accepts a target inner object. It must forward all undefined attribute lookups and method calls to this nested object automatically using dynamic delegation.",
      explanation: "Dynamic delegation avoids the need to write repetitive boilerplate code when wrapping external libraries. Intercepting lookups at runtime lets you wrap and extend existing classes cleanly.",
      hint: "Override the `__getattr__` method to call the corresponding attribute on your internal target object.",
      starterCode: `class AttributeProxy:\n    def __init__(self, target_obj):\n        self._target = target_obj\n\n    def __getattr__(self, name):\n        # your code here\n        pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "", call: "class T: val = 1\nAttributeProxy(T()).val", expected: "1" },
      ],
    },
    {
      id: 87,
      title: "Command Pattern Undoable Invoker Engine",
      slug: "python-command-pattern-undoable-invoker-engine",
      seoTitle: "Python Command Pattern State History | Design Patterns | Repractiq",
      metaDescription: "Implement a history system with undo support by encapsulating actions as objects with the Command pattern.",
      tags: ["design patterns", "advanced OOP"],
      difficulty: "Hard",
      type: "function",
      description: "Build an event-driven system using the Command pattern. Create an invoker called `TransactionHistory` that accepts Command objects (which support `execute()` and `undo()`), maintaining a history stack to support undoing operations sequentially.",
      explanation: "The Command pattern wraps user actions inside separate objects. Storing these objects in a stack allows you to easily roll back changes and implement undo support in your applications.",
      hint: "Keep a list of executed command objects, popping and running `undo()` on the latest command when requested.",
      starterCode: `class Command:\n    def execute(self): pass\n    def undo(self): pass\n\nclass TransactionHistory:\n    def __init__(self):\n        self.history = []\n\n    def run_command(self, cmd):\n        # your code here\n        pass\n\n    def undo_last(self):\n        # your code here\n        pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "", call: "hasattr(TransactionHistory, 'undo_last')", expected: "True" },
      ],
    },
    {
      id: 88,
      title: "High-Performance Binary Search Lookup",
      slug: "python-high-performance-binary-search-lookup",
      seoTitle: "Python Bisect Lookup List Speed | Performance optimization | Repractiq",
      metaDescription: "Speed up search operations on sorted lists by replacing slow linear searches with optimized binary lookups.",
      tags: ["performance optimization"],
      difficulty: "Hard",
      type: "function",
      description: "Write a high-performance lookup function called `fast_search` that checks if a target value exists inside a large sorted list using the built-in `bisect` module. It must perform much faster than a standard linear `in` check.",
      explanation: "Using the `in` operator on lists runs in linear O(N) time. If the list is already sorted, applying binary search via the `bisect` module drops the lookup time to O(log N), speeding up searches on large datasets.",
      hint: "Use `bisect.bisect_left` to find where the element should be, and verify if that index matches your search target.",
      starterCode: `import bisect\n\ndef fast_search(sorted_list, target_val):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "[1, 3, 5], 3", call: "fast_search([1, 3, 5], 3)", expected: "True" },
      ],
    },
    {
      id: 89,
      title: "Garbage Collection Circular Reference Detector",
      slug: "python-garbage-collection-circular-reference-detector",
      seoTitle: "Python Circular Reference GC Diagnostics | Memory management | Repractiq",
      metaDescription: "Detect and debug memory leaks by using Python's gc module to track circular object reference chains.",
      tags: ["memory management"],
      difficulty: "Hard",
      type: "function",
      description: "Write a diagnostic helper called `has_circular_reference` that takes an object and checks if it belongs to an active circular reference loop, identifying potential memory leaks.",
      explanation: "While reference counting handles most cleanup, circular references (where objects reference each other) require the cyclic garbage collector. Tracking these relationships helps prevent memory leaks in your applications.",
      hint: "Use `gc.get_referents()` and `gc.get_referrers()` to inspect object references and search for circular dependencies.",
      starterCode: `import gc\n\ndef has_circular_reference(obj):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "", call: "str(type(has_circular_reference([])))", expected: "<class 'bool'>" },
      ],
    },
    {
      id: 90,
      title: "Recursive JSON Key Path Searcher",
      slug: "python-recursive-json-key-path-searcher",
      seoTitle: "Python Recursive Nested Dict Search | Advanced recursion | Repractiq",
      metaDescription: "Locate specific keys within deeply nested JSON structures and return their full path recursively.",
      tags: ["advanced recursion", "nested data structures"],
      difficulty: "Hard",
      type: "function",
      description: "Write a recursive function called `find_key_path` that searches a deeply nested dictionary for a target key. It must return a list of parent keys representing the path to the target, or `None` if the key is not found.",
      explanation: "Searching for keys in deeply nested dictionaries is difficult with standard loops. Recursion simplifies this by diving into child dictionaries and building the path back to the top-level parent once the target is found.",
      hint: "Iterate through keys. If a key matches, return a list containing it. If a value is another dictionary, recursively call the function and append the current key if found.",
      starterCode: `def find_key_path(nested_dict, target_key):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "{'a': {'b': {'c': 1}}}, 'c'", call: "find_key_path({'a': {'b': {'c': 1}}}, 'c')", expected: "['a', 'b', 'c']" },
      ],
    },
    {
      id: 91,
      title: "Retry Decorator with Exponential Backoff",
      slug: "python-retry-decorator-exponential-backoff",
      seoTitle: "Python Retry Backoff Exception Decorator | Decorators | Repractiq",
      metaDescription: "Build resilient integrations by wrapping functions with a decorator that retries failing operations using exponential backoff delays.",
      tags: ["decorators"],
      difficulty: "Hard",
      type: "function",
      description: "Create a parameterized decorator called `retry_with_backoff(max_attempts, base_delay)` that retries a function if it raises an exception, increasing the delay between attempts exponentially.",
      explanation: "Temporary network and API issues can cause functions to fail. A retry decorator with exponential backoff waits longer after each failed attempt, preventing you from overloading external servers while allowing the system time to recover.",
      hint: "Use a loop inside your wrapper that catches exceptions and sleeps for `base_delay * (2 ** attempt)` seconds before retrying.",
      starterCode: `import time\nimport functools\n\ndef retry_with_backoff(max_attempts, base_delay):\n    def decorator(func):\n        # your code here\n        return func\n    return decorator\n`,
      expectedOutput: "",
      testCases: [
        { input: "", call: "hasattr(retry_with_backoff(3, 0.1), '__call__')", expected: "True" },
      ],
    },
    {
      id: 92,
      title: "Finite State Machine Generator Router",
      slug: "python-finite-state-machine-generator-router",
      seoTitle: "Python Generator FSM State Transitions | Generators | Repractiq",
      metaDescription: "Build a clean, memory-efficient finite state machine using generators and state-transition logic.",
      tags: ["generators"],
      difficulty: "Hard",
      type: "function",
      description: "Write a generator function called `state_machine_tracker` that acts as a finite state machine. It must start in the `'INIT'` state, transition to and yield matching states as inputs are sent, and raise an error if an invalid transition occurs.",
      explanation: "Generators can maintain internal state across multiple steps, making them great for building simple state machines. Using `yield` to receive inputs lets you coordinate transitions without writing complex tracking logic.",
      hint: "Use an infinite loop containing `input_val = yield current_state` and update the state according to your transition rules.",
      starterCode: `def state_machine_tracker():\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "", call: "g = state_machine_tracker(); next(g)", expected: "INIT" },
      ],
    },
    {
      id: 93,
      title: "Stdout Redirection Capture Context Manager",
      slug: "python-stdout-redirection-capture-context-manager",
      seoTitle: "Python Redirect Sys Stdout StringIO | Context Managers | Repractiq",
      metaDescription: "Intercept and capture standard console output streams inside isolated context blocks for verification.",
      tags: ["context managers"],
      difficulty: "Hard",
      type: "function",
      description: "Create a context manager called `CaptureStdout` using the `@contextmanager` decorator. It must redirect `sys.stdout` to a temporary `io.StringIO` buffer, yielding the stream so that any console output can be captured and read.",
      explanation: "Testing functions that print directly to the console requires intercepting standard output. Redirecting `sys.stdout` inside a context manager lets you capture console output cleanly without affecting the rest of the application.",
      hint: "Save the original `sys.stdout` in a variable, assign `sys.stdout = StringIO()`, yield the buffer, and restore `sys.stdout` inside a `finally` block.",
      starterCode: `import sys\nimport io\nfrom contextlib import contextmanager\n\n@contextmanager\ndef CaptureStdout():\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "", call: "with CaptureStdout() as f: print('test')\nf.getvalue().strip()", expected: "test" },
      ],
    },
    {
      id: 94,
      title: "Auto-Serialization Metaclass Compiler",
      slug: "python-auto-serialization-metaclass-compiler",
      seoTitle: "Python Metaclass Auto Serialization Dict | Metaclasses concepts | Repractiq",
      metaDescription: "Inject helper methods into class definitions automatically using custom metaclasses.",
      tags: ["metaclasses concepts", "design patterns"],
      difficulty: "Hard",
      type: "function",
      description: "Write a metaclass called `SerializableMeta` that automatically adds a `to_dict()` method to any class it creates, converting the class's public attributes into a standard dictionary.",
      explanation: "Writing repetitive serialization code for every data model is tedious. A metaclass can inject this functionality automatically, ensuring all classes conform to a standard serialization interface.",
      hint: "Implement a serialize function and assign it to `dct['to_dict']` during class creation inside the metaclass.",
      starterCode: `class SerializableMeta(type):\n    def __new__(cls, name, bases, dct):\n        # your code here\n        return super().__new__(cls, name, bases, dct)\n`,
      expectedOutput: "",
      testCases: [
        { input: "", call: "class Model(metaclass=SerializableMeta):\n def __init__(self): self.id=1\nModel().to_dict()", expected: "{'id': 1}" },
      ],
    },
    {
      id: 95,
      title: "Async Gather with Partial Success Resilience",
      slug: "python-async-gather-partial-success-resilience",
      seoTitle: "Python Asyncio Gather Return Exceptions | Async basics | Repractiq",
      metaDescription: "Run asynchronous tasks concurrently while gathering successful results, ensuring that individual task failures do not crash the entire process.",
      tags: ["async basics"],
      difficulty: "Hard",
      type: "function",
      description: "Write an asynchronous orchestrator called `gather_resilient` that takes a list of tasks. It must execute them concurrently and return their results, capturing any exceptions as string errors inside the returned list instead of crashing.",
      explanation: "By default, `asyncio.gather` stops and raises an exception if any task fails. Setting `return_exceptions=True` allows you to gather results from successful tasks while safely collecting any error details.",
      hint: "Pass `return_exceptions=True` to `asyncio.gather` to collect exceptions alongside successful results.",
      starterCode: `import asyncio\n\nasync def gather_resilient(tasks_list):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "", call: "import asyncio; asyncio.iscoroutinefunction(gather_resilient)", expected: "True" },
      ],
    },
    {
      id: 96,
      title: "Dynamic Custom Property Generator",
      slug: "python-dynamic-custom-property-generator",
      seoTitle: "Python Setattr Property Descriptor | Advanced OOP | Repractiq",
      metaDescription: "Generate custom properties with built-in validation dynamically on classes at runtime.",
      tags: ["advanced OOP"],
      difficulty: "Hard",
      type: "function",
      description: "Write a utility function called `add_validated_property(cls, name, validator_func)` that dynamically attaches a validated property to an existing class, rejecting values that fail the validator function.",
      explanation: "In Python, you can modify class attributes dynamically. Attaching properties programmatically lets you enforce validation rules at runtime without hardcoding getters and setters into every class.",
      hint: "Use `property(fget, fset)` to create a new property object, and attach it to the class using `setattr(cls, name, prop)`.",
      starterCode: `def add_validated_property(cls, name, validator_func):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "", call: "class Test: pass\nadd_validated_property(Test, 'x', lambda v: v > 0)\nt = Test(); t.x = 5; t.x", expected: "5" },
      ],
    },
    {
      id: 97,
      title: "State Design Pattern Document Workflow",
      slug: "python-state-design-pattern-document-workflow",
      seoTitle: "Python State Pattern Context Class | Design Patterns | Repractiq",
      metaDescription: "Manage complex object state transitions cleanly using the State design pattern.",
      tags: ["design patterns", "advanced OOP"],
      difficulty: "Hard",
      type: "function",
      description: "Design a system using the State pattern. Create a `Document` class that transitions its internal state (`DraftState`, `ReviewState`, `PublishedState`), allowing or blocking edits based on the active state.",
      explanation: "Using nested conditional checks to manage complex object states can quickly make your code difficult to maintain. The State pattern avoids this by outsourcing state-specific logic to dedicated state classes.",
      hint: "Have each state class implement the editing and transition logic, and update the document's state reference dynamically.",
      starterCode: `class State:\n    def write(self, text): pass\n\nclass Document:\n    def __init__(self):\n        self.state = DraftState()\n        self.content = ''\n\n    def write(self, text):\n        # your code here\n        pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "", call: "hasattr(Document, 'write')", expected: "True" },
      ],
    },
    {
      id: 98,
      title: "Using Map/Filter vs Comprehensions Analyzer",
      slug: "python-using-map-filter-vs-comprehensions-analyzer",
      seoTitle: "Python Map Filter List Comprehension Benchmark | Performance optimization | Repractiq",
      metaDescription: "Analyze and optimize data processing speed by comparing list comprehensions with map and filter functions.",
      tags: ["performance optimization"],
      difficulty: "Hard",
      type: "function",
      description: "Write a utility function called `benchmark_transform` that compares the speed of list comprehensions against `map()` and `filter()` functions when applying operations to large lists, returning the fastest method.",
      explanation: "List comprehensions are generally faster because they run optimized bytecode internally. Comparing execution times helps you choose the best iteration approach for high-frequency data loops.",
      hint: "Use the `time` or `timeit` modules to measure execution times for both processing approaches.",
      starterCode: `import time\n\ndef benchmark_transform(large_dataset):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "", call: "str(type(benchmark_transform(list(range(1000)))))", expected: "<class 'str'>" },
      ],
    },
    {
      id: 99,
      title: "Object Pool Pattern Allocator",
      slug: "python-object-pool-pattern-allocator",
      seoTitle: "Python Object Pool Resource Recycler | Memory management | Repractiq",
      metaDescription: "Optimize performance and reduce memory usage by building an Object Pool to reuse heavy resource instances.",
      tags: ["memory management", "design patterns"],
      difficulty: "Hard",
      type: "function",
      description: "Create an allocator pool class called `ResourcePool` that manages a fixed set of reusable connection objects, allowing clients to checkout and return resources to prevent memory fragmentation.",
      explanation: "Constantly creating and destroying heavy objects can cause memory issues and trigger frequent garbage collection pauses. An object pool keeps a cache of initialized objects ready for reuse, improving performance.",
      hint: "Use a queue or list to track active and idle connections, implementing checkout and return methods.",
      starterCode: `class ResourcePool:\n    def __init__(self, size):\n        self.pool = []\n        # your code here\n\n    def acquire(self):\n        # your code here\n        pass\n\n    def release(self, item):\n        # your code here\n        pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "", call: "hasattr(ResourcePool(5), 'acquire')", expected: "True" },
      ],
    },
    {
      id: 100,
      title: "Recursive Deep Merge of Dictionaries",
      slug: "python-recursive-deep-merge-of-dictionaries",
      seoTitle: "Python Recursive Dict Merge Union | Advanced recursion | Repractiq",
      metaDescription: "Deeply merge nested dictionary configurations recursively, handling key conflicts cleanly.",
      tags: ["advanced recursion", "nested data structures"],
      difficulty: "Hard",
      type: "function",
      description: "Write a recursive merge utility called `deep_merge_dict` that combines two deeply nested dictionaries. If a key conflict occurs and both values are dictionaries, they must be merged recursively; otherwise, the value from the second dictionary overrides the first.",
      explanation: "Merging complex, nested configurations requires more than a simple `.update()`, which only replaces top-level keys. A recursive merge function goes down every level of nested structures, merging matching branches safely.",
      hint: "Loop through keys. If a key is present in both dictionaries and both values are dictionaries, recursively call `deep_merge_dict` on them.",
      starterCode: `def deep_merge_dict(dict1, dict2):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "{'a': {'b': 1}}, {'a': {'c': 2}}", call: "str(deep_merge_dict({'a': {'b': 1}}, {'a': {'c': 2}}))", expected: "{'a': {'b': 1, 'c': 2}}" },
      ],
    },
];